import {ChangeEvent, Fragment, FormEvent, useEffect, useRef, useState} from 'react';
import './FenzSafeApp.css';
import {useBrowserFileDownload} from './useBrowserFileDownload';
import {FenzSafeViewDocumentButton} from './FenzSafeDocumentViewer';
import {FenzSafeSummaryDraftControl} from './FenzSafeSummaryDraftPanel';
import {
  DOCUMENT_CATEGORIES,
  DocumentCategory,
  documentIndexCsv,
  documentVersionChain,
  FENZSAFE_DOCUMENT_MAX_BYTES,
  fenzsafeStore,
  FenzSafeRepresentationStatus,
  groupDocumentsByCategory,
  groupPhoneEvidenceBySource,
  isFenzSafeAdapterReal,
  isUsingRealFenzSafeBackend,
  MATTER_WATCH_TRIGGER_TYPES,
  Matter,
  matterReadinessFlags,
  MatterTier,
  MatterWatchTriggerType,
  OrganizeSuggestion,
  triageCounts,
  TRIAGE_STATES,
  TriageState,
  useFenzSafe,
} from './fenzsafeStore';
import {friendlyMutationError} from './friendlyMutationError';
import {ChunkedUploadProgress, FenzSafeChunkedUploadError, retryCommitUploadSession, shouldAttemptChunkedUpload, uploadDocumentBytesChunked} from './fenzsafeChunkedUpload';
import {FenzSafePacketPrintPreview} from './FenzSafePacketPrintPreview';
import {FenzSafeDiscoveryPanel} from './FenzSafeDiscoveryPanel';
import {HolmesJrGuide} from './HolmesJrGuide';
import {FenzSafeJurisdictionGuidancePanel} from './FenzSafeJurisdictionGuidancePanel';
import {FenzSafeEmailImport} from './FenzSafeEmailImport';
import {provenanceGlyph, provenanceKindOf, provenanceLabel, verificationStatusGlyph, verificationStatusLabel, verificationStatusOf} from './fenzsafeVerificationContract';
import {clearPendingTextMessageHandoff, readPendingTextMessageHandoff} from './fenzsafeTextMessageHandoff';

const PROCESSING_LABEL: Record<string, string> = {
  awaiting_upload: 'Awaiting upload',
  accepted: 'Ready',
  pending_scan: 'Scanning',
  quarantined: 'Blocked',
  duplicate: 'Duplicate',
  too_large: 'Too large',
  unavailable: 'Unavailable',
};

function documentExpectedVersion(matter: Matter, documentId: string): number {
  const document = matter.documents.find((item) => item.id === documentId);
  if (!document) throw new Error('The document is no longer available. Reload and try again.');
  return document.rowVersion ?? matter.version;
}

function tooLargeMessage(file: File): string {
  return `${file.name} is too large. Choose a file up to ${Math.floor(FENZSAFE_DOCUMENT_MAX_BYTES / (1024 * 1024))} MB.`;
}

function formatChunkedUploadPercent(progress: ChunkedUploadProgress): string {
  if (progress.totalBytes <= 0) return '0%';
  const percent = Math.min(100, Math.floor((progress.sentBytes / progress.totalBytes) * 100));
  return `${percent}%`;
}

/**
 * Docket #37 Stage 1c + Docket #192 item B6: pulled out as its own pure function (same convention
 * as formatChunkedUploadPercent()/tooLargeMessage() above) so the priority order between a stuck
 * retryable large-file upload and everything else Holmes Jr. already surfaces is directly
 * unit-testable. Component regressions separately verify that the call site scopes upload
 * counts to the selected matter without discarding another matter's resumable session.
 */
export function holmesJrWorkspaceTip(params: {
  jurisdictionEscalation: string | null;
  retryableChunkedSessionCount: number;
  pendingEvidenceRequestCount: number;
  organizeSuggestionCount: number;
  topReadinessFlagMessage: string | null;
  activeChunkedUploadCount: number;
}): string {
  if (params.jurisdictionEscalation) return `Jurisdiction guidance for this matter needs a person to review -- ${params.jurisdictionEscalation}`;
  if (params.retryableChunkedSessionCount > 0) {
    const count = params.retryableChunkedSessionCount;
    return `${count === 1 ? 'A large file upload' : `${count} large file uploads`} didn't finish. You can complete ${count === 1 ? 'it' : 'them'} without choosing the file again -- see below.`;
  }
  if (params.pendingEvidenceRequestCount > 0) {
    const count = params.pendingEvidenceRequestCount;
    return `Your attorney asked for ${count === 1 ? 'one more thing' : `${count} more things`} -- see below.`;
  }
  if (params.organizeSuggestionCount > 0) {
    const count = params.organizeSuggestionCount;
    return `I found ${count === 1 ? 'one document that' : `${count} documents that`} might be filed under a better category -- see below.`;
  }
  if (params.topReadinessFlagMessage) return params.topReadinessFlagMessage;
  if (params.activeChunkedUploadCount > 0) {
    const count = params.activeChunkedUploadCount;
    return `${count === 1 ? 'A large file is' : `${count} large files are`} uploading in the background. You can keep working while ${count === 1 ? 'it finishes' : 'they finish'}.`;
  }
  return 'Everything here is organized. You can create an attorney package whenever you are ready.';
}

const FRIENDLY_TIMESTAMP_FORMAT = new Intl.DateTimeFormat('en-US', {dateStyle: 'medium', timeStyle: 'short'});
// The real backend sends every timestamp (Matter.createdAt, FenzSafeDocument.addedAt, timeline
// entries, attorney-package openedAt/revokedAt) as a raw `(string) $row['created_at']` cast of
// whatever PDO fetched from a DATETIME(6) column (fenzsafeBackendAdapter.ts never reformats it) --
// e.g. "2026-09-26 08:24:34.584628": no "T", no zone, and a microsecond fraction Date.parse() does
// not reliably handle the same way across engines. The synthetic adapter instead seeds these
// fields with an already-friendly fixed string ("Just now (synthetic)", "Sept 8, 2026 · 6:42 PM
// ET"), which happens to be why this went unnoticed -- every demo/test render uses that adapter.
// This repo's whole stack runs in UTC (see docs/operations/schema-migration-governance.md), so a
// bare "YYYY-MM-DD HH:MM:SS[.ffffff]" string is always that wall-clock time in UTC.
/**
 * Codex independent-review finding (PR #295, comment 5908123499): every FenzSafeChunkedUploadError
 * carrying an uploadSessionId was marked retryable, including an append failure (whose session
 * uploadDocumentBytesChunked() already aborted, best-effort, before rethrowing) and a hard commit
 * failure such as fenzsafe_upload_session.integrity_mismatch -- both then showed "Finish upload" as
 * if resumable. Only a scanner-outage commit failure is actually safe to retry without re-sending
 * bytes (see fenzsafeChunkedUpload.ts's own retryCommitUploadSession() doc comment). Exported as its
 * own pure predicate so this exact decision has direct, focused test coverage without needing a full
 * real-adapter upload integration harness (fenzsafeChunkedUpload.ts and its test file stay pinned
 * unchanged from PR #274, so this lives here rather than there).
 */
export function isRetryableChunkedUploadError(error: unknown): error is FenzSafeChunkedUploadError & {uploadSessionId: string} {
  return error instanceof FenzSafeChunkedUploadError && Boolean(error.uploadSessionId) && error.code === 'fenzsafe_upload_session.scanner_unavailable';
}

const RAW_SQL_TIMESTAMP = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(\.\d+)?$/;
export function friendlyTimestamp(value: string): string {
  const match = value.match(RAW_SQL_TIMESTAMP);
  if (!match) return value; // already-friendly synthetic fixture text -- shown unchanged.
  const parsed = new Date(`${match[1]}T${match[2]}Z`);
  if (Number.isNaN(parsed.getTime())) return value; // fail open: never hide the real value.
  return FRIENDLY_TIMESTAMP_FORMAT.format(parsed);
}

/**
 * Explains a stuck-in-place processing status and why the fix is a new version rather than a
 * same-document retry. pending_scan: the backend's own state machine has no in-place "retry the
 * scan" transition for a document already at pending_scan (a deliberate, already-reviewed
 * limitation -- see DocumentController::registerBytes()'s doc comment and
 * FenzSafeDocumentProcessingStatus::canTransition()), so re-attaching the same bytes to the same
 * document id fails closed with a confusing conflict; adding a new version is the real, already-
 * supported recovery path instead. quarantined/unavailable: retrying the identical rejected/gone
 * bytes cannot change the outcome, so the copy must not imply that it would.
 */
const STUCK_DOCUMENT_EXPLANATION: Record<'pending_scan' | 'quarantined' | 'unavailable', string> = {
  pending_scan: "This document's security scan did not finish (the scanner was temporarily unavailable). This exact upload attempt can't be retried in place — upload the file again as a new version to try once more.",
  quarantined: 'This document failed the required security scan and was not accepted. Uploading the exact same file again will not change that result — if you have a corrected file, add it as a new version.',
  unavailable: "This document's stored file is no longer available and can't be downloaded. If you still have the original file, add it as a new version.",
};

const RELEVANCE_LABEL: Record<string, string> = {
  relevant: 'Relevant',
  not_relevant: 'Not relevant',
  needs_review: 'Needs review',
};

const TIER_LABEL: Record<MatterTier, string> = {
  free: 'Free organizer',
  one_time: 'One-time prep pass',
  monthly: 'Monthly case workspace',
};

/**
 * Positioning only, not a feature/price list -- specific plan features and pricing haven't been
 * decided by the business yet (upgradeTier() only changes this label today, nothing is gated on
 * it). Keeping this to directional intent avoids the UI committing to feature promises or prices
 * that aren't real yet.
 */
const TIER_PITCH: Record<MatterTier, string> = {
  free: 'Organize one matter at no cost and share an attorney-ready package when you’re done.',
  one_time: 'Extra hands-on help getting a single matter attorney-ready, fast.',
  monthly: 'Ongoing organization and support for as long as the case stays open.',
};

/** Plain-language, no-lawyer-assumed explanations -- many FenzSafe users are pro se litigants who have never seen "pleadings" or "correspondence" used this way before. */
const CATEGORY_HELP: Record<DocumentCategory, string> = {
  'Court notice': 'Something the court itself sent you — a hearing date, a summons, or a notice telling you when to appear.',
  Pleadings: 'Formal papers filed with the court that state a claim or ask the judge to do something — like a complaint, an answer, or a motion.',
  Correspondence: 'Letters, emails, or texts between you and the other side, their lawyer, or a landlord/property manager.',
  Evidence: 'Photos, receipts, texts, or anything else that helps show what actually happened.',
  Research: 'Notes or information you gathered on your own, like tenant-rights articles or local court rules.',
  Other: "Anything that doesn't fit the categories above.",
};

type PhoneItemType = 'messages' | 'photo' | 'video' | 'call_log';

const PHONE_ITEM_TYPE_LABEL: Record<PhoneItemType, string> = {
  messages: 'Message thread',
  photo: 'Photo or screenshot',
  video: 'Video',
  call_log: 'Call log',
};

const PHONE_ITEM_TYPE_SUFFIX: Record<PhoneItemType, string> = {
  messages: 'text-thread.json',
  photo: 'photo.heic',
  video: 'video.mov',
  call_log: 'call-log.csv',
};

function downloadCsv(matter: Matter, isRealBackend: boolean) {
  const csv = documentIndexCsv(matter, isRealBackend);
  const url = URL.createObjectURL(new Blob([csv], {type: 'text/csv'}));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `fenzsafe-${matter.reference}-document-index.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * Issue #126 comment `5813058999` (corrected to the exact52 floor -- M52 accepted production truth
 * -- by comment `5813513291`): whether each schema/runtime-gated FenzSafe capability is actually
 * usable right now in this environment (a later migration than exact52 gates each one on the real
 * backend, and documentsReady additionally requires the storage provider to be configured AND the
 * document scanner to be genuinely reachable with current signatures right now, not just the table
 * to exist (Issue #192 STOP/CORRECT `5822652449`) -- see
 * FenzSafeSchemaReadiness.php/FenzSafeStorageSelector.php/DocumentScanner::healthy(). pricingReady is
 * similarly two-layered (Issue #192 STOP/CORRECT review `5308022461` + comment `5820198188`): its
 * backing table (M54) is deliberately installed empty, so this is true only once at least one
 * non-free tier also has a real, governed active offer published -- table presence alone must never
 * expose the switch-plan/upgrade control. Absent (the
 * synthetic walkthrough, and every existing bare `<FenzSafeApp/>` test render) defaults every flag
 * to true, preserving prior behavior exactly; the real mount (FenzSafeWorkspace.tsx) always supplies
 * the real, honest values read from the authenticated application context.
 */
/** matterSummaryReady is optional so existing callers stay unchanged; absent means false. */
export type FenzSafeCapabilities = {documentsReady: boolean; attorneySharesReady: boolean; evidenceRequestsReady: boolean; pricingReady: boolean; representationReady: boolean; matterWatchReady: boolean; chunkedUploadReady: boolean; matterSummaryReady?: boolean};
// Issue #977: the AI summary draft is deliberately NOT "ready" in the demo/guest/walkthrough default --
// there is no synthetic or sample summary anywhere.
const ALL_CAPABILITIES_READY: FenzSafeCapabilities = {documentsReady: true, attorneySharesReady: true, evidenceRequestsReady: true, pricingReady: true, representationReady: true, matterWatchReady: true, chunkedUploadReady: true, matterSummaryReady: false};

const MATTER_WATCH_TRIGGER_LABELS: Record<MatterWatchTriggerType, string> = {
  complaint: 'Complaint',
  summons: 'Summons',
  subpoena: 'Subpoena',
  eviction_notice: 'Eviction notice',
  domestication: 'Domestication',
};

export function FenzSafeApp({onPreviewAsAttorney, capabilities = ALL_CAPABILITIES_READY, accountScope}: {onPreviewAsAttorney?: (matterId: string) => void; capabilities?: FenzSafeCapabilities; accountScope?: string} = {}) {
  // Issue #192 human-usability walkthrough finding (2026-09-26): this used to be `!onPreviewAsAttorney`,
  // on the theory that only the attorney-preview toggle (FenzSafeWalkthrough.tsx) ever renders this
  // component against non-real data. That stopped being true once a second synthetic mount point
  // existed -- the guest-first entry point (FenzSafeGuestEntry.tsx, /fenzsafe/try) -- which also
  // passes no onPreviewAsAttorney prop, so it showed "Free to start / saved to your account"
  // directly underneath its own banner saying the opposite ("Nothing you organize here is saved
  // yet"). isUsingRealFenzSafeBackend() reflects the actual fenzsafeStore adapter binding
  // (FenzSafeWorkspace.tsx's setFenzSafeAdapter() call) instead of inferring it from an unrelated prop.
  const isRealBackend = isUsingRealFenzSafeBackend();
  // The public guest entry and the developer walkthrough both use the local adapter, but only the
  // walkthrough supplies the attorney-preview callback. User-entered guest work is real to that
  // visitor even though it is browser-local; never describe it as synthetic fixture data.
  const isGuestExperience = !isRealBackend && onPreviewAsAttorney === undefined;
  const state = useFenzSafe();
  const [activeMatterId, setActiveMatterId] = useState<string | null>(state.matters.find((matter) => !matter.archived)?.id ?? null);
  // useState's initializer above only ever runs on this component's first render -- it can't
  // pick up matters that arrive after that (a real backend adapter loads them over the network,
  // asynchronously; the synthetic store instead seeds them synchronously before React ever
  // renders, so this only ever surfaced once a real adapter existed to test against). Without
  // this effect, a matter that is still loading at mount time is permanently invisible: the
  // onboarding screen shows forever even once the fetch resolves and state.matters is populated.
  // Only steps in when the current selection is missing/archived, so it never overrides an
  // explicit user choice (switching matters, restoring an archived one, creating a new one).
  useEffect(() => {
    if (activeMatterId !== null && state.matters.some((matter) => matter.id === activeMatterId && !matter.archived)) return;
    const fallback = state.matters.find((matter) => !matter.archived);
    if (fallback) setActiveMatterId(fallback.id);
  }, [state.matters, activeMatterId]);
  useEffect(() => {
    setSelectedShareDocIds(new Set());
    setShowUpdateSharePanel(false);
    setJurisdictionEscalation(null);
    setSkippedSuggestionIds(new Set());
    setReviewedSuggestionIds(new Set());
    setOrganizeReviewCounts({accepted: 0, edited: 0, skipped: 0});
    setOrganizeEditSelections({});
  }, [activeMatterId]);
  const [matterName, setMatterName] = useState('');
  const [matterReference, setMatterReference] = useState('');
  const [nameError, setNameError] = useState('');
  const [newMatterRepresentation, setNewMatterRepresentation] = useState<FenzSafeRepresentationStatus | null>(null);
  const [showNewMatterFlow, setShowNewMatterFlow] = useState(false);
  const [newDocName, setNewDocName] = useState('');
  const [newDocCategory, setNewDocCategory] = useState<DocumentCategory>('Other');
  const [phoneSourceLabel, setPhoneSourceLabel] = useState('');
  const [phoneItemLabel, setPhoneItemLabel] = useState('');
  const [phoneItemType, setPhoneItemType] = useState<PhoneItemType>('messages');
  const [error, setError] = useState('');
  const {startDownload, downloadStatus} = useBrowserFileDownload(activeMatterId);
  const [lastStatus, setLastStatus] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [expandedDocId, setExpandedDocId] = useState<string | null>(null);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  // Surfaces a jurisdiction-guidance result that needs human review through Holmes Jr.'s tip
  // (Issue #164 founder continuation directive, item 3: "surface relevant jurisdiction/rules
  // information only when supported by the rules/resource seam"). Null whenever nothing has been
  // checked yet, or the last check didn't require escalation -- FenzSafeJurisdictionGuidancePanel
  // is the only writer, via its onEscalation callback.
  const [jurisdictionEscalation, setJurisdictionEscalation] = useState<string | null>(null);
  const [confirmRemoveDocId, setConfirmRemoveDocId] = useState<string | null>(null);
  const [editingMatterId, setEditingMatterId] = useState<string | null>(null);
  const isEditingMatter = editingMatterId !== null && editingMatterId === activeMatterId;
  const [editMatterName, setEditMatterName] = useState('');
  const [editMatterReference, setEditMatterReference] = useState('');
  const [editMatterNameError, setEditMatterNameError] = useState('');
  const [editRepresentationStatus, setEditRepresentationStatus] = useState<FenzSafeRepresentationStatus | null>(null);
  const activeMatterIdRef = useRef(activeMatterId);
  // Explicit attorney-share document selection (Issue #114, Slice C document-selection UI) --
  // never auto-selected from the matter's documents; reset whenever the active matter changes so
  // a stale selection from a previous matter can never leak into a new create()/update call.
  const [selectedShareDocIds, setSelectedShareDocIds] = useState<Set<string>>(new Set());
  const [showUpdateSharePanel, setShowUpdateSharePanel] = useState(false);
  // Which existing document the customer has chosen, per pending evidence request, before
  // clicking "Provide this" -- keyed by request id since a matter can have several requests
  // pending at once, each needing its own independent selection.
  const [evidenceRequestSelections, setEvidenceRequestSelections] = useState<Record<string, string>>({});
  const [resolvingRequestId, setResolvingRequestId] = useState<string | null>(null);
  const [matterWatchPartyNames, setMatterWatchPartyNames] = useState('');
  const [matterWatchCourt, setMatterWatchCourt] = useState('');
  const [matterWatchCounty, setMatterWatchCounty] = useState('');
  const [matterWatchTriggerTypes, setMatterWatchTriggerTypes] = useState<MatterWatchTriggerType[]>([]);
  const [matterWatchBusy, setMatterWatchBusy] = useState(false);
  const matterWatchBusyRef = useRef(false);
  // Organize-suggestions review (Issue #192 Docket #17 Stage C). Accept/edit are already durable
  // the moment they happen (they go through the same updateCategory() -> updateDocument() path as
  // any manual category change), but a backend refresh racing with the next render can still show
  // a just-accepted/edited suggestion for one more tick -- and an edit that chooses a *different*
  // category than the one originally suggested never makes the backend's own suggestion disappear
  // at all, since its heuristic only suppresses an exact suggested-category match (Codex STOP/CORRECT
  // review 5327607777, finding 3). "Skip" has no server-side effect to record per document either --
  // it is purely "don't show me this one again this session." All three are therefore tracked here,
  // client-side only, as two DELIBERATELY SEPARATE kinds of state with different lifecycles:
  // - skippedSuggestionIds/reviewedSuggestionIds are permanent, session-long "don't show me this
  //   document's suggestion again" visibility records. They are NEVER reset by "Done reviewing"
  //   itself (finding 3's second half: Done used to clear skippedSuggestionIds and immediately
  //   resurrect every skipped suggestion still in the same panel) -- only switching matters (the
  //   effect above) clears either set, since there is nothing server-side to re-derive either from.
  // - organizeReviewCounts is the resettable "not yet reported to the backend" tally -- it DOES
  //   reset to zero after a successful "Done reviewing" (a skip/accept/edit already reported must
  //   never be counted again on the next save). Conflating the two (deriving the reported "skipped"
  //   number from skippedSuggestionIds.size, as an earlier version of this fix did) would silently
  //   re-report the same skip on every subsequent save, forever, once the set stopped being cleared.
  const [skippedSuggestionIds, setSkippedSuggestionIds] = useState<Set<string>>(new Set());
  const [reviewedSuggestionIds, setReviewedSuggestionIds] = useState<Set<string>>(new Set());
  const [organizeReviewCounts, setOrganizeReviewCounts] = useState({accepted: 0, edited: 0, skipped: 0});
  const [organizeEditSelections, setOrganizeEditSelections] = useState<Record<string, DocumentCategory>>({});
  const [applyingSuggestionId, setApplyingSuggestionId] = useState<string | null>(null);
  const [completingOrganizeReview, setCompletingOrganizeReview] = useState(false);
  // A single synchronous mutual-exclusion boundary for the whole organize-review session: accept,
  // edit and "Done reviewing" each read and write the same organizeReviewCounts/skipped/reviewed
  // state, so gating them on two independent flags (applyingSuggestionId, completingOrganizeReview)
  // let either one start while the other was still in flight (Codex STOP/CORRECT review 5327607777,
  // finding 2) -- e.g. Done reading and posting counts before an in-flight accept's own increment
  // had landed, silently losing it. A ref (not state) makes the check-and-set atomic across the
  // synchronous portion of each handler, unlike two separate state reads that could both observe
  // "not busy" before either commits.
  const organizeReviewBusyRef = useRef(false);
  // A record formatted on the free, unauthenticated text-message tool and handed off via
  // sessionStorage (Issue #114 founder direction, comment 5774592884, CE priority #6) -- read once
  // at mount, matching Application.tsx's own "explicit handoff is a one-time arrival context" note
  // for the ProFenz handoff. Cleared only on a successful add or an explicit discard, never
  // silently, so a visitor who isn't signed in yet still sees it once they do sign in and land here
  // (sessionStorage survives that same-tab navigation).
  const [pendingTextMessageHandoff, setPendingTextMessageHandoff] = useState(() => readPendingTextMessageHandoff());
  const statusRef = useRef<HTMLParagraphElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const newMatterHeadingRef = useRef<HTMLHeadingElement>(null);
  const newMatterTriggerRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (showNewMatterFlow) newMatterHeadingRef.current?.focus();
  }, [showNewMatterFlow]);
  function closeNewMatterFlow() {
    setShowNewMatterFlow(false);
    newMatterTriggerRef.current?.focus();
  }
  const fileInputRef = useRef<HTMLInputElement>(null);
  const versionFileInputRef = useRef<HTMLInputElement>(null);
  const attachBytesFileInputRef = useRef<HTMLInputElement>(null);
  // Guards a same-document double-invocation landing before isBusy (a shared, unrelated-mutation
  // flag) happens to be true -- downloadDocument() doesn't itself set isBusy, so nothing else
  // prevents a rapid double-click from starting two overlapping downloads of the same file.
  const downloadsInFlight = useRef<Set<string>>(new Set());
  // Documents currently mid-flight between addDocument()/addDocumentVersion() and
  // registerDocumentBytes() resolving -- shown as a distinct "Uploading…" badge rather than
  // whatever transient processingStatus (awaiting_upload) the document briefly sits at server-side.
  const [uploadingDocIds, setUploadingDocIds] = useState<Set<string>>(new Set());
  // Issue #697/#747 UX-rule audit (findings A1): a per-document write-in-progress guard shared by
  // the quick inline triage/category/relevance controls, plus a separate per-matter guard for the
  // notification checkbox. These previously had no in-progress guard at all, so a rapid second
  // click/change before the first write resolved could fire an overlapping request against a
  // now-stale expected version. A single shared set (not one per field) is required because
  // updateTriage/updateCategory/markRelevance all read documentExpectedVersion() -- the SAME
  // optimistic-concurrency token on the document record, not a per-field one -- so editing two
  // different fields on one document concurrently races on that shared version exactly as two
  // edits to the same field would. Caught by review after an earlier revision guarded each field
  // independently, which stopped a same-field double-click but not a cross-field race (e.g.
  // clicking the category select and the triage select for the same document within the same
  // in-flight window). Disjoint from uploadingDocIds/isBusy -- this must not lock the whole page
  // the way a matter-level mutation does, only the one document's own field controls.
  // Independent review correction: the useState Set above is read inside each handler as
  // `pendingFieldEditDocIds.has(documentId)` -- a render snapshot, not a synchronous value. Two of
  // these handlers invoked back-to-back (e.g. two fast clicks, or one click landing while another
  // field's change event is still in the same microtask queue) before React has applied the first
  // setPendingFieldEditDocIds(...) update can both read the pre-update Set and both see `false`,
  // so both proceed and both submit against the same stale documentExpectedVersion() -- the exact
  // cross-field race this guard exists to prevent, just moved one layer down. A ref is checked and
  // set synchronously within the same call stack (see isCreatingMatterRef above for the same fix
  // applied to createMatter), so the second handler's check always observes the first handler's
  // set. The useState Set is kept, but now only to drive `disabled={...}` on the controls -- it is
  // no longer what blocks a second write.
  const pendingFieldEditRef = useRef<Set<string>>(new Set());
  const [pendingFieldEditDocIds, setPendingFieldEditDocIds] = useState<Set<string>>(new Set());
  // Same synchronous-ref mutex as pendingFieldEditRef above, for the same reason: the
  // pendingNotifyMatterIds useState Set below is a render snapshot, not a synchronous value, so
  // two rapid toggles of the same checkbox (e.g. two synchronous fireEvent.click calls in a test,
  // or a fast double-click) can both read the pre-update Set before React commits the first
  // setPendingNotifyMatterIds(...) call, and both submit an updateNotificationPreference write
  // against the same stale matter.version -- the same class of race the field-edit mutex above
  // was added to close. Independent review correction (PR #764).
  const pendingNotifyRef = useRef<Set<string>>(new Set());
  const [pendingNotifyMatterIds, setPendingNotifyMatterIds] = useState<Set<string>>(new Set());
  // Docket #37 Stage 1c: a file over FENZSAFE_DOCUMENT_MAX_BYTES uploads through
  // uploadDocumentBytesChunked() (PR #274's client) instead of the single-shot
  // registerDocumentBytes() call above -- this map tracks its live sent/total bytes so the
  // existing "Uploading…" badge can show progress instead of a bare spinner state.
  const [chunkedUploadProgress, setChunkedUploadProgress] = useState<Map<string, ChunkedUploadProgress>>(new Map());
  // A chunked upload that fails after every chunk was already accepted (e.g. a scanner-outage 503
  // from commit()) leaves a server-side session that retryCommitUploadSession() can finish without
  // re-sending any bytes -- see that function's own doc comment. Recorded only while that specific
  // retry is genuinely still possible for that document.
  const [retryableChunkedSessions, setRetryableChunkedSessions] = useState<Map<string, string>>(new Map());
  // One AbortController per in-flight chunked upload so the visible "Cancel" action can actually
  // stop the network calls -- a ref (not state) because it never needs to trigger a re-render on
  // its own; the state maps above already do that.
  const chunkedUploadAbortControllers = useRef<Map<string, AbortController>>(new Map());
  // A useState-only guard isn't enough for a synchronous rapid double-click (React batches the
  // state update from the click handler); a ref checked-and-set synchronously is what actually
  // blocks a second createMatter call before the first one resolves.
  const isCreatingMatterRef = useRef(false);
  // Stable across a failed createMatter() retry of the *same* unchanged form content (Issue #114
  // recovery-hardening finding): every mutation elsewhere in this file generates a fresh
  // crypto.randomUUID() inline at the call site, which is fine for a mutation guarded by the
  // matter's own expected_version (a lost-response retry just hits a conflict) but not for
  // createMatter() -- there is no prior matter to version-check against. If the create actually
  // succeeded server-side but the response never reached this tab (a network drop), a retry with a
  // brand-new key would be indistinguishable from a genuinely new matter and create a duplicate;
  // MatterRepository::createIdempotently()'s own INSERT-IGNORE-and-re-SELECT only protects a retry
  // that reuses the same key. Regenerated only when the form's actual content changes (below) --
  // editing the name/reference/role after a failure is a new intent and must not silently resolve
  // to whatever matter an earlier, different-content attempt already created.
  const createMatterIdempotencyKeyRef = useRef(crypto.randomUUID());
  useEffect(() => {
    createMatterIdempotencyKeyRef.current = crypto.randomUUID();
  }, [matterName, matterReference, newMatterRepresentation]);

  function focusStatusSoon() {
    requestAnimationFrame(() => statusRef.current?.focus());
  }

  function markUploading(documentId: string) {
    setUploadingDocIds((prev) => new Set(prev).add(documentId));
  }

  function unmarkUploading(documentId: string) {
    setUploadingDocIds((prev) => {
      const next = new Set(prev);
      next.delete(documentId);
      return next;
    });
  }

  function clearChunkedProgress(documentId: string) {
    setChunkedUploadProgress((prev) => {
      if (!prev.has(documentId)) return prev;
      const next = new Map(prev);
      next.delete(documentId);
      return next;
    });
  }

  function clearRetryableChunkedSession(documentId: string) {
    setRetryableChunkedSessions((prev) => {
      if (!prev.has(documentId)) return prev;
      const next = new Map(prev);
      next.delete(documentId);
      return next;
    });
  }

  function noteChunkedUploadFailure(documentId: string, error: unknown) {
    if (isRetryableChunkedUploadError(error)) {
      setRetryableChunkedSessions((prev) => new Map(prev).set(documentId, (error as FenzSafeChunkedUploadError).uploadSessionId as string));
    } else {
      clearRetryableChunkedSession(documentId);
    }
  }

  /**
   * Docket #37 Stage 1c: uploads a file over FENZSAFE_DOCUMENT_MAX_BYTES via
   * uploadDocumentBytesChunked() (PR #274's already-reviewed client) instead of the single-shot
   * registerDocumentBytes() call, ending at the identical PENDING_SCAN -> accepted/quarantined
   * outcome. That client never touches fenzsafeStore's own subscriber/cache mechanism (it is a
   * deliberately self-contained fetch-based module -- see its own doc comment), so this refreshes
   * the matter afterward to pull the committed document's real status into the reactive store,
   * matching reportUploadFailure()'s existing refresh convention below. Rethrows on any failure so
   * the caller's existing reportUploadFailure() handling (unchanged) still runs.
   */
  async function uploadLargeFile(matterId: string, documentId: string, expectedVersion: number, file: File): Promise<void> {
    const controller = new AbortController();
    chunkedUploadAbortControllers.current.set(documentId, controller);
    setChunkedUploadProgress((prev) => new Map(prev).set(documentId, {sentBytes: 0, totalBytes: file.size}));
    try {
      await uploadDocumentBytesChunked(documentId, expectedVersion, file, {
        signal: controller.signal,
        onProgress: (progress) => setChunkedUploadProgress((prev) => new Map(prev).set(documentId, progress)),
      });
      clearRetryableChunkedSession(documentId);
      await fenzsafeStore.refreshMatter(matterId);
    } catch (error) {
      noteChunkedUploadFailure(documentId, error);
      throw error;
    } finally {
      chunkedUploadAbortControllers.current.delete(documentId);
      clearChunkedProgress(documentId);
    }
  }

  function cancelChunkedUpload(documentId: string) {
    chunkedUploadAbortControllers.current.get(documentId)?.abort();
  }

  /**
   * Finishes an interrupted chunked upload without re-sending any bytes -- only reachable while
   * retryableChunkedSessions still has an entry for this document (a genuine post-commit-failure
   * retry target; see uploadLargeFile()'s catch clause above). Shares handleFileSelected's own
   * uploading-badge/error-reporting conventions so this looks identical to any other upload
   * failure/retry from the user's side.
   */
  async function retryChunkedUpload(matter: Matter, documentId: string) {
    const uploadSessionId = retryableChunkedSessions.get(documentId);
    if (!uploadSessionId || isBusy) return;
    setError('');
    setIsBusy(true);
    markUploading(documentId);
    try {
      await retryCommitUploadSession(uploadSessionId);
      clearRetryableChunkedSession(documentId);
      await fenzsafeStore.refreshMatter(matter.id);
      setLastStatus('Your upload finished and is being reviewed.');
      focusStatusSoon();
    } catch (thrown) {
      noteChunkedUploadFailure(documentId, thrown);
      await reportUploadFailure(matter.id, documentId, 'this file', thrown);
    } finally {
      unmarkUploading(documentId);
      setIsBusy(false);
    }
  }

  const activeMatter = state.matters.find((matter) => matter.id === activeMatterId && !matter.archived);
  useEffect(() => {
    activeMatterIdRef.current = activeMatterId;
    setEditRepresentationStatus(activeMatter?.representationStatus ?? null);
  }, [activeMatterId, activeMatter?.representationStatus]);
  const archivedMatters = state.matters.filter((matter) => matter.archived);

  const archivedSection = archivedMatters.length > 0 && (
    <section className="fsa-panel" aria-labelledby="fsa-archived-heading">
      <h2 id="fsa-archived-heading">Archived matters</h2>
      <ul className="fsa-archived-list">
        {archivedMatters.map((matter) => (
          <li key={matter.id}>
            <span>
              {matter.name} <span className="fsa-doc-source">· Reference {matter.reference}</span>
            </span>
            <button type="button" className="fsa-secondary" onClick={() => restoreMatter(matter)} disabled={isBusy} aria-label={`Restore ${matter.name}`}>
              Restore
            </button>
          </li>
        ))}
      </ul>
    </section>
  );

  async function createMatter(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isCreatingMatterRef.current) return;
    if (!matterName.trim()) {
      setNameError('Enter a name for this matter.');
      return;
    }
    setNameError('');
    isCreatingMatterRef.current = true;
    setIsBusy(true);
    try {
      const createdName = matterName.trim();
      const id = await fenzsafeStore.createMatter(matterName.trim(), matterReference.trim(), newMatterRepresentation, createMatterIdempotencyKeyRef.current);
      setActiveMatterId(id);
      setMatterName('');
      setMatterReference('');
      setNewMatterRepresentation(null);
      setShowNewMatterFlow(false);
      // Issue #697/#747 A4: focus already moved to the new matter's own heading, but nothing was
      // ever announced and no next step was suggested -- the live-status region now names the
      // outcome and points at the natural next action (zero-scavenger-scroll).
      setLastStatus(`${createdName} created. Add your first document below.`);
      requestAnimationFrame(() => headingRef.current?.focus());
    } catch (thrown) {
      setError(friendlyMutationError(thrown, 'Could not create this matter. Try again.'));
    } finally {
      isCreatingMatterRef.current = false;
      setIsBusy(false);
    }
  }

  async function handleFileSelected(matter: Matter, event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    // A file over the single-shot cap only has a real path forward against the real backend --
    // the synthetic/guest in-browser store has no chunked-session endpoint to reach, so it keeps
    // exactly the prior flat rejection. This must check isFenzSafeAdapterReal(), not the
    // isRealBackend/isUsingRealFenzSafeBackend() presentational flag: that flag can be true while
    // fenzsafeStore still calls through to the synthetic adapter (setUsingRealFenzSafeBackendForTests
    // does exactly this for most of this component's own tests), and the chunked-upload client
    // below bypasses the adapter to `fetch` its own endpoints directly, so it would otherwise fire
    // real HTTP requests against the synthetic walkthrough/guest preview.
    //
    // Issue #192 STOP finding (PR #295 review `5975211800`): isFenzSafeAdapterReal() alone is not
    // enough -- it only says the real (non-synthetic) adapter is bound, never whether
    // RouterFactory actually constructed ChunkedUploadSessionController for this environment
    // (gated behind the installed upload-sessions schema, a configured durable storage provider,
    // AND an explicit local-prototype opt-in env var -- see RouterFactory.php's own
    // $fenzsafeUploadSessionsReady comment). Without also checking capabilities.chunkedUploadReady
    // -- the server-authoritative fact threaded through application context exactly like every
    // other fenzsafe_*_ready capability -- a real customer's file over the cap would attempt a
    // chunked upload against a route that was never registered, trading today's clean "too large"
    // message for a worse generic upload failure.
    const useChunkedUpload = shouldAttemptChunkedUpload(file.size, FENZSAFE_DOCUMENT_MAX_BYTES, isFenzSafeAdapterReal(), capabilities.chunkedUploadReady);
    if (file.size > FENZSAFE_DOCUMENT_MAX_BYTES && !useChunkedUpload) {
      setError(tooLargeMessage(file));
      return;
    }
    setError('');
    setIsBusy(true);
    let createdId: string | undefined;
    try {
      const afterAdd = await fenzsafeStore.addDocument(matter.id, matter.version, {name: file.name, category: newDocCategory, source: 'upload', sizeKb: Math.max(1, Math.round(file.size / 1024))}, crypto.randomUUID());
      // The just-created document sorts last -- both adapters append/order new documents by
      // creation time (see fenzsafeStore.ts/fenzsafeBackendAdapter.ts's own addDocument() doc
      // comments), and nothing else can have created a document for this matter between the
      // two calls in this single-actor flow.
      const created = afterAdd.documents[afterAdd.documents.length - 1];
      createdId = created.id;
      markUploading(created.id);
      if (useChunkedUpload) {
        await uploadLargeFile(matter.id, created.id, documentExpectedVersion(afterAdd, created.id), file);
      } else {
        await fenzsafeStore.registerDocumentBytes(matter.id, afterAdd.version, created.id, file, crypto.randomUUID());
      }
      if (activeMatterIdRef.current === matter.id) {
        setLastStatus(isGuestExperience
          ? `${file.name} was added in this browser and is ready to organize.`
          : `${file.name} was uploaded and is being reviewed.`);
        focusStatusSoon();
      }
    } catch (thrown) {
      await reportUploadFailure(matter.id, createdId, file.name, thrown);
    } finally {
      if (createdId) unmarkUploading(createdId);
      setIsBusy(false);
    }
  }

  async function importEmailFile(matter:Matter,file:File):Promise<boolean>{
    if(isBusy)return false;setError('');setIsBusy(true);let createdId:string|undefined;
    try{const afterAdd=await fenzsafeStore.addDocument(matter.id,matter.version,{name:file.name,category:'Correspondence',source:'upload',sizeKb:Math.max(1,Math.round(file.size/1024))},crypto.randomUUID());const created=afterAdd.documents[afterAdd.documents.length-1];createdId=created.id;markUploading(created.id);await fenzsafeStore.registerDocumentBytes(matter.id,afterAdd.version,created.id,file,crypto.randomUUID());if(activeMatterIdRef.current===matter.id){setLastStatus(`${file.name} was imported as correspondence and is being reviewed.`);focusStatusSoon();}return true;}
    catch(thrown){await reportUploadFailure(matter.id,createdId,file.name,thrown,()=>activeMatterIdRef.current===matter.id);return false;}
    finally{if(createdId)unmarkUploading(createdId);setIsBusy(false);}
  }

  /**
   * Shared by every call site that can fail after a document row already exists (handleFileSelected/
   * addVersion's addDocument()/addDocumentVersion() step succeeded but registerDocumentBytes()
   * itself then threw, or attachBytes()'s registerDocumentBytes() call on an existing document
   * failed outright). registerDocumentBytes() never returns a Matter on any failure path -- even a
   * real, durable one like a rejected/quarantined scan or a scanner-unavailable outcome -- so the
   * local cache is left showing the document's pre-attempt status (e.g. "awaiting_upload") unless
   * something re-fetches it. Best-effort: a failed refresh here must never hide the original upload
   * error behind a second, unrelated one.
   */
  async function reportUploadFailure(matterId: string, documentId: string | undefined, fileName: string, thrown: unknown, shouldDisplay: () => boolean = () => true) {
    const message = `"${fileName}": ${friendlyMutationError(thrown, 'could not be uploaded. See its status below for what to do next.')}`;
    if (!documentId) {
      if (shouldDisplay() && activeMatterIdRef.current === matterId) setError(message);
      return;
    }
    try {
      await fenzsafeStore.refreshMatter(matterId);
    } catch {
      // The document's true post-failure status couldn't be confirmed -- the error below still
      // tells the user what happened; expanding it shows whatever status is already known.
    }
    // Refresh still targets the original matter; a switch during that await must
    // not transfer its upload result to the newly visible matter.
    if (!shouldDisplay() || activeMatterIdRef.current !== matterId) return;
    setExpandedDocId(documentId);
    setError(message);
  }

  /**
   * Turns a formatter-originated handoff into a real document through the exact same two-call
   * canonical path as handleFileSelected() above (addDocument() then registerDocumentBytes()), so
   * its bytes/hash/timestamp identity are established by the real backend, not synthesized here --
   * per Issue #114 comment 5774592884's own requirement that this "enter the matter as evidence/
   * document content through the canonical FenzSafe document path... not as a synthetic-only row."
   * Only ever called from this explicit "Add to this matter" click; nothing before it touches the
   * network. Errors reuse reportUploadFailure()'s existing convention of naming only the file name,
   * never the record's own text, so a failure/retry can never leak message content.
   */
  async function addTextMessageHandoffDocument(matter: Matter) {
    if (!pendingTextMessageHandoff || isBusy) return;
    const handoff = pendingTextMessageHandoff;
    setError('');
    setIsBusy(true);
    const file = new File([handoff.documentText], handoff.fileName, {type: 'text/plain'});
    let createdId: string | undefined;
    try {
      const afterAdd = await fenzsafeStore.addDocument(matter.id, matter.version, {name: handoff.fileName, category: 'Evidence', source: 'upload', sizeKb: Math.max(1, Math.round(file.size / 1024))}, crypto.randomUUID());
      const created = afterAdd.documents[afterAdd.documents.length - 1];
      createdId = created.id;
      markUploading(created.id);
      await fenzsafeStore.registerDocumentBytes(matter.id, afterAdd.version, created.id, file, crypto.randomUUID());
      clearPendingTextMessageHandoff();
      setPendingTextMessageHandoff(null);
      if (activeMatterIdRef.current === matter.id) {
        setLastStatus('Your text-message record was added to this matter and is being reviewed.');
        focusStatusSoon();
      }
    } catch (thrown) {
      await reportUploadFailure(matter.id, createdId, handoff.fileName, thrown);
    } finally {
      if (createdId) unmarkUploading(createdId);
      setIsBusy(false);
    }
  }

  function discardTextMessageHandoff() {
    clearPendingTextMessageHandoff();
    setPendingTextMessageHandoff(null);
  }

  async function archiveMatter(matter: Matter) {
    if (isBusy) return; // No overlapping archive on a rapid double-click.
    setError('');
    setIsBusy(true);
    try {
      await fenzsafeStore.archiveMatter(matter.id, matter.version, crypto.randomUUID());
      const remaining = state.matters.find((item) => item.id !== matter.id && !item.archived);
      setActiveMatterId(remaining?.id ?? null);
      // Issue #697/#747 A2: this used to be a silent success -- the view just switched to another
      // matter (or the empty onboarding state) with nothing naming what happened or where to undo
      // it. Both render branches below now carry the same live-status region (see the !activeMatter
      // return), so the polite live region announces this whichever branch renders next. No explicit
      // focus move here (unlike e.g. addDocument's focusStatusSoon()) -- archiving always re-renders
      // a *different* view (another matter or the onboarding screen) that the person may act on
      // immediately afterward, and a deferred requestAnimationFrame focus call landing after that
      // next action started would steal focus back to the status text rather than respect it.
      setLastStatus(`${matter.name} archived. Restore it from Archived matters.`);
    } catch (thrown) {
      setError(friendlyMutationError(thrown, 'Could not archive this matter. Try again.'));
    } finally {
      setIsBusy(false);
    }
  }

  async function restoreMatter(matter: Matter) {
    if (isBusy) return; // No overlapping restore on a rapid double-click.
    setError('');
    setIsBusy(true);
    try {
      await fenzsafeStore.restoreMatter(matter.id, matter.version, crypto.randomUUID());
      setActiveMatterId(matter.id);
      // Issue #697/#747 A3: previously announced nothing -- success was visible only as a focus/
      // visual change (the heading appearing). The focus move to the restored matter's own heading
      // is kept as-is; the live-status region (always present in the matter-workspace render below)
      // now also names the outcome for anyone not watching the heading change.
      setLastStatus(`${matter.name} restored.`);
      requestAnimationFrame(() => headingRef.current?.focus());
    } catch (thrown) {
      setError(friendlyMutationError(thrown, 'Could not restore this matter. Try again.'));
    } finally {
      setIsBusy(false);
    }
  }

  function startEditingMatter(matter: Matter) {
    setEditMatterName(matter.name);
    setEditMatterReference(matter.reference);
    setEditMatterNameError('');
    setEditingMatterId(matter.id);
  }

  async function saveMatterEdits(matter: Matter) {
    if (isBusy || editingMatterId !== matter.id || activeMatterIdRef.current !== matter.id) return;
    if (!editMatterName.trim()) {
      setEditMatterNameError('Enter a name for this matter.');
      return;
    }
    setError('');
    setIsBusy(true);
    try {
      await fenzsafeStore.renameMatter(matter.id, matter.version, {name: editMatterName.trim(), reference: editMatterReference.trim()}, crypto.randomUUID());
      setEditingMatterId(current => current === matter.id ? null : current);
      // Issue #697/#747 A3: previously announced nothing at all -- the edit form just closed back
      // to the read view with no receipt that the save actually happened.
      setLastStatus('Matter details saved.');
      focusStatusSoon();
    } catch (thrown) {
      if (activeMatterIdRef.current === matter.id) {
        setError(friendlyMutationError(thrown, 'Could not update this matter. Try again.'));
      }
    } finally {
      setIsBusy(false);
    }
  }

  async function addDocument(matter: Matter) {
    if (!newDocName.trim()) return;
    setError('');
    setIsBusy(true);
    try {
      await fenzsafeStore.addDocument(matter.id, matter.version, {name: newDocName.trim(), category: newDocCategory, source: 'upload', sizeKb: 0}, crypto.randomUUID());
      setNewDocName('');
      setLastStatus(`${newDocName.trim()} added to your organizer.`);
      focusStatusSoon();
    } catch (thrown) {
      setError(friendlyMutationError(thrown, 'Could not add that document. Try again.'));
    } finally {
      setIsBusy(false);
    }
  }

  async function importPhoneItem(matter: Matter) {
    if (!phoneItemLabel.trim() || !phoneSourceLabel.trim()) return;
    setError('');
    setIsBusy(true);
    const slug = phoneItemLabel.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'export';
    const name = `${slug}-${PHONE_ITEM_TYPE_SUFFIX[phoneItemType]}`;
    try {
      await fenzsafeStore.addDocument(matter.id, matter.version, {name, category: 'Evidence', source: 'phone_import', sizeKb: 0, phoneSource: phoneSourceLabel.trim()}, crypto.randomUUID());
      // The source stays filled in -- the next item is usually from the same export.
      setPhoneItemLabel('');
      setLastStatus(`${name} added by name from ${phoneSourceLabel.trim()} and flagged for a relevance review. No file is attached yet.`);
      focusStatusSoon();
    } catch (thrown) {
      setError(friendlyMutationError(thrown, 'Could not import that item. Try again.'));
    } finally {
      setIsBusy(false);
    }
  }

  async function updateTriage(matter: Matter, documentId: string, triageState: TriageState) {
    // Issue #697/#747 A1 + follow-up correction: clear a stale error from an earlier attempt
    // before this one starts, and refuse an overlapping write on the same document -- from this
    // or any of the other two field edits below, since all three share one optimistic-concurrency
    // version token (documentExpectedVersion) rather than racing against their own.
    if (pendingFieldEditRef.current.has(documentId)) return;
    const documentName = matter.documents.find((doc) => doc.id === documentId)?.name ?? 'Document';
    setError('');
    pendingFieldEditRef.current.add(documentId);
    setPendingFieldEditDocIds((prev) => new Set(prev).add(documentId));
    try {
      await fenzsafeStore.updateDocument(matter.id, documentId, documentExpectedVersion(matter, documentId), {triageState}, crypto.randomUUID());
      // Named receipt in the live status region, but no focus move -- this is a quick inline
      // control, and stealing focus away from it mid-review would be more disruptive than helpful.
      if (activeMatterIdRef.current === matter.id) setLastStatus(`${documentName} marked ${triageState.toLowerCase()}.`);
    } catch (thrown) {
      if (activeMatterIdRef.current === matter.id) setError(friendlyMutationError(thrown, 'Could not update that document. Try again.'));
    } finally {
      pendingFieldEditRef.current.delete(documentId);
      setPendingFieldEditDocIds((prev) => {
        const next = new Set(prev);
        next.delete(documentId);
        return next;
      });
    }
  }

  async function updateCategory(matter: Matter, documentId: string, category: DocumentCategory) {
    if (pendingFieldEditRef.current.has(documentId)) return;
    const documentName = matter.documents.find((doc) => doc.id === documentId)?.name ?? 'Document';
    setError('');
    pendingFieldEditRef.current.add(documentId);
    setPendingFieldEditDocIds((prev) => new Set(prev).add(documentId));
    try {
      await fenzsafeStore.updateDocument(matter.id, documentId, documentExpectedVersion(matter, documentId), {category}, crypto.randomUUID());
      if (activeMatterIdRef.current === matter.id) setLastStatus(`${documentName} moved to ${category}.`);
    } catch (thrown) {
      if (activeMatterIdRef.current === matter.id) setError(friendlyMutationError(thrown, 'Could not update that document. Try again.'));
    } finally {
      pendingFieldEditRef.current.delete(documentId);
      setPendingFieldEditDocIds((prev) => {
        const next = new Set(prev);
        next.delete(documentId);
        return next;
      });
    }
  }

  function triggerVersionUpload(documentId: string) {
    const input = versionFileInputRef.current;
    if (!input) return;
    input.dataset.forDoc = documentId;
    input.click();
  }

  async function addVersion(matter: Matter, documentId: string, event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > FENZSAFE_DOCUMENT_MAX_BYTES) {
      setError(tooLargeMessage(file));
      return;
    }
    setError('');
    setIsBusy(true);
    let createdId: string | undefined;
    try {
      const afterAdd = await fenzsafeStore.addDocumentVersion(matter.id, documentId, documentExpectedVersion(matter, documentId), {name: file.name, sizeKb: Math.max(1, Math.round(file.size / 1024))}, crypto.randomUUID());
      // Same "just-created document sorts last" reasoning as handleFileSelected() above.
      const created = afterAdd.documents[afterAdd.documents.length - 1];
      createdId = created.id;
      markUploading(created.id);
      await fenzsafeStore.registerDocumentBytes(matter.id, afterAdd.version, created.id, file, crypto.randomUUID());
      if (activeMatterIdRef.current === matter.id) {
        setLastStatus(`${file.name} added as a new version and is being reviewed. The prior version is kept, marked replaced.`);
        focusStatusSoon();
      }
    } catch (thrown) {
      await reportUploadFailure(matter.id, createdId, file.name, thrown);
    } finally {
      if (createdId) unmarkUploading(createdId);
      setIsBusy(false);
    }
  }

  function triggerAttachBytes(documentId: string) {
    const input = attachBytesFileInputRef.current;
    if (!input) return;
    input.dataset.forDoc = documentId;
    input.click();
  }

  /** Registers real bytes for a document that already exists at awaiting_upload -- the "add by name only" affordance's own follow-up, since that path never reads a file to begin with. */
  async function attachBytes(matter: Matter, documentId: string, event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > FENZSAFE_DOCUMENT_MAX_BYTES) {
      setError(tooLargeMessage(file));
      return;
    }
    setError('');
    setIsBusy(true);
    markUploading(documentId);
    try {
      await fenzsafeStore.registerDocumentBytes(matter.id, matter.version, documentId, file, crypto.randomUUID());
      if (activeMatterIdRef.current === matter.id) {
        setLastStatus(isGuestExperience
          ? `${file.name} was added in this browser and is ready to organize.`
          : `${file.name} was uploaded and is being reviewed.`);
        focusStatusSoon();
      }
    } catch (thrown) {
      await reportUploadFailure(matter.id, documentId, file.name, thrown);
    } finally {
      unmarkUploading(documentId);
      setIsBusy(false);
    }
  }

  async function downloadDocument(documentId: string, name: string) {
    if (downloadsInFlight.current.has(documentId)) return;
    downloadsInFlight.current.add(documentId);
    setError('');
    try {
      const blob = await fenzsafeStore.downloadDocumentBytes(documentId);
      startDownload(blob, name, isRealBackend ? `/api/v1/fenzsafe/documents/${documentId}/bytes` : undefined);
    } catch (thrown) {
      // Prefixes the specific document's name onto whatever the real error says -- with more than
      // one document in the table, the underlying message alone (from either adapter, e.g. "The
      // document could not be found.") never names which one failed.
      // friendlyMutationError() itself passes a real Error's own .message straight through
      // unchanged (its fallback argument only fires for a non-Error thrown value), so the name
      // has to be added here rather than folded into that fallback.
      setError(`"${name}": ${friendlyMutationError(thrown, 'could not be downloaded. Try again.')}`);
    } finally {
      downloadsInFlight.current.delete(documentId);
    }
  }

  async function removeDocument(matter: Matter, documentId: string, name: string) {
    setError('');
    try {
      await fenzsafeStore.removeDocument(matter.id, documentId, documentExpectedVersion(matter, documentId), crypto.randomUUID());
      setConfirmRemoveDocId(null);
      setExpandedDocId(null);
      setLastStatus(`${name} removed from the organizer.`);
      focusStatusSoon();
    } catch (thrown) {
      setError(friendlyMutationError(thrown, 'Could not remove that document. Try again.'));
    }
  }

  async function markRelevance(matter: Matter, documentId: string, evidenceRelevance: 'relevant' | 'not_relevant') {
    if (pendingFieldEditRef.current.has(documentId)) return;
    const documentName = matter.documents.find((doc) => doc.id === documentId)?.name ?? 'Document';
    setError('');
    pendingFieldEditRef.current.add(documentId);
    setPendingFieldEditDocIds((prev) => new Set(prev).add(documentId));
    try {
      await fenzsafeStore.updateDocument(matter.id, documentId, documentExpectedVersion(matter, documentId), {evidenceRelevance}, crypto.randomUUID());
      if (activeMatterIdRef.current === matter.id) setLastStatus(`${documentName} marked ${evidenceRelevance.replace('_', ' ')}.`);
    } catch (thrown) {
      if (activeMatterIdRef.current === matter.id) setError(friendlyMutationError(thrown, 'Could not update that item. Try again.'));
    } finally {
      pendingFieldEditRef.current.delete(documentId);
      setPendingFieldEditDocIds((prev) => {
        const next = new Set(prev);
        next.delete(documentId);
        return next;
      });
    }
  }

  async function fulfillEvidenceRequest(matter: Matter, requestId: string, requestVersion: number) {
    const documentId = evidenceRequestSelections[requestId];
    if (!documentId || resolvingRequestId !== null) return;
    setError('');
    setResolvingRequestId(requestId);
    try {
      await fenzsafeStore.fulfillEvidenceRequest(matter.id, requestId, requestVersion, documentId, crypto.randomUUID());
      setEvidenceRequestSelections((current) => {
        const {[requestId]: _removed, ...rest} = current;
        return rest;
      });
      setLastStatus('Sent to your attorney.');
      focusStatusSoon();
    } catch (thrown) {
      setError(friendlyMutationError(thrown, 'Could not send that document. Try again.'));
    } finally {
      setResolvingRequestId(null);
    }
  }

  async function declineEvidenceRequest(matter: Matter, requestId: string, requestVersion: number) {
    if (resolvingRequestId !== null) return;
    setError('');
    setResolvingRequestId(requestId);
    try {
      await fenzsafeStore.declineEvidenceRequest(matter.id, requestId, requestVersion, undefined, crypto.randomUUID());
      setLastStatus('Marked as not available.');
      focusStatusSoon();
    } catch (thrown) {
      setError(friendlyMutationError(thrown, 'Could not update that request. Try again.'));
    } finally {
      setResolvingRequestId(null);
    }
  }

  async function acceptOrganizeSuggestion(matter: Matter, suggestion: OrganizeSuggestion) {
    if (organizeReviewBusyRef.current) return;
    const matterId = matter.id;
    organizeReviewBusyRef.current = true;
    setError('');
    setApplyingSuggestionId(suggestion.documentId);
    try {
      await fenzsafeStore.updateDocument(matterId, suggestion.documentId, documentExpectedVersion(matter, suggestion.documentId), {category: suggestion.suggestedCategory}, crypto.randomUUID());
      // A switch away from this matter while the mutation was in flight must never attribute this
      // accept to whichever matter is active now (Codex STOP/CORRECT review 5327607777, finding 1).
      if (activeMatterIdRef.current !== matterId) return;
      setOrganizeReviewCounts((current) => ({...current, accepted: current.accepted + 1}));
      setReviewedSuggestionIds((current) => new Set(current).add(suggestion.documentId));
      setLastStatus(`Moved to ${suggestion.suggestedCategory}.`);
      focusStatusSoon();
    } catch (thrown) {
      if (activeMatterIdRef.current !== matterId) return;
      setError(friendlyMutationError(thrown, 'Could not update that document. Try again.'));
    } finally {
      setApplyingSuggestionId(null);
      organizeReviewBusyRef.current = false;
    }
  }

  async function applyOrganizeSuggestionEdit(matter: Matter, suggestion: OrganizeSuggestion) {
    const chosenCategory = organizeEditSelections[suggestion.documentId];
    if (!chosenCategory || organizeReviewBusyRef.current) return;
    const matterId = matter.id;
    organizeReviewBusyRef.current = true;
    setError('');
    setApplyingSuggestionId(suggestion.documentId);
    try {
      await fenzsafeStore.updateDocument(matterId, suggestion.documentId, documentExpectedVersion(matter, suggestion.documentId), {category: chosenCategory, triageState: 'Reviewed'}, crypto.randomUUID());
      if (activeMatterIdRef.current !== matterId) return;
      setOrganizeReviewCounts((current) => ({...current, edited: current.edited + 1}));
      // Suppress this suggestion for the rest of the session regardless of which category was
      // actually chosen -- the backend's own suppression only fires when the document's category
      // now matches the *suggested* one, so choosing a different category (the whole point of
      // "edit") would otherwise leave it reappearing every refresh (finding 3).
      setReviewedSuggestionIds((current) => new Set(current).add(suggestion.documentId));
      setOrganizeEditSelections((current) => {
        const {[suggestion.documentId]: _removed, ...rest} = current;
        return rest;
      });
      setLastStatus(`Moved to ${chosenCategory}.`);
      focusStatusSoon();
    } catch (thrown) {
      if (activeMatterIdRef.current !== matterId) return;
      setError(friendlyMutationError(thrown, 'Could not update that document. Try again.'));
    } finally {
      setApplyingSuggestionId(null);
      organizeReviewBusyRef.current = false;
    }
  }

  function skipOrganizeSuggestion(documentId: string) {
    // Done/accept/edit all share one synchronous boundary. A Skip event already queued before
    // React paints the disabled state must still be a no-op, or Done can report the old count and
    // then erase the newly-added skip when its confirmed write resets the counters.
    if (organizeReviewBusyRef.current) return;
    // skippedSuggestionIds is the permanent visibility record (never reset by Done); the count
    // reported to the backend lives in organizeReviewCounts instead, which Done DOES reset -- see
    // the state declaration's doc comment for why conflating the two would re-report the same skip
    // on every subsequent save.
    setSkippedSuggestionIds((current) => new Set(current).add(documentId));
    setOrganizeReviewCounts((current) => ({...current, skipped: current.skipped + 1}));
  }

  async function completeOrganizeReview(matter: Matter) {
    // organizeReviewBusyRef also gates acceptOrganizeSuggestion()/applyOrganizeSuggestionEdit(), so
    // this can never actually start while one of those is still in flight -- the counts read below
    // are therefore always fully settled, never a mid-mutation snapshot (finding 2).
    if (organizeReviewBusyRef.current) return;
    const matterId = matter.id;
    const reportedCounts = organizeReviewCounts;
    organizeReviewBusyRef.current = true;
    setError('');
    setCompletingOrganizeReview(true);
    try {
      const writeConfirmed = await fenzsafeStore.completeOrganizeSuggestions(matterId, reportedCounts.accepted, reportedCounts.edited, reportedCounts.skipped, crypto.randomUUID())
        .then(() => true)
        .catch((thrown: unknown) => {
          // The backend endpoint this posts to has no idempotency handling (see
          // fenzsafeBackendAdapter.ts's own documented limitation) -- a naive "Try again" after ANY
          // failure would silently resend {accepted, edited, skipped} and double-count them if the
          // write itself had actually already succeeded and only the follow-up refresh failed. The
          // adapter's own refresh_failed code (matched by name/code, not instanceof, same convention
          // friendlyMutationError.ts already uses -- each adapter module defines its own identically
          // named local error class to avoid a module cycle) is how it tells us which case this is;
          // only that exact code means the write is durable and must never be resent.
          if (thrown instanceof Error && thrown.name === 'FenzSafeBackendError' && (thrown as Error & {code?: string}).code === 'fenzsafe_organize_suggestions.refresh_failed') return true;
          throw thrown;
        });
      if (activeMatterIdRef.current !== matterId) return;
      if (writeConfirmed) {
        // The reported accepted/edited/skipped counts are now durably recorded server-side -- reset
        // them so the next save only reports what happens after this point. skippedSuggestionIds/
        // reviewedSuggestionIds (the permanent VISIBILITY sets, separate from this resettable COUNT)
        // are deliberately NOT cleared here: they are a client-only "don't show me this again this
        // session" record with nothing server-side to re-derive it from, so clearing them would
        // immediately resurrect every already-dismissed suggestion still present in the same panel
        // (finding 3's second half). Only switching matters clears them.
        setOrganizeReviewCounts({accepted: 0, edited: 0, skipped: 0});
        setLastStatus('Organizing review saved.');
        focusStatusSoon();
      }
    } catch (thrown) {
      if (activeMatterIdRef.current !== matterId) return;
      setError(friendlyMutationError(thrown, 'Could not save that review. Try again.'));
    } finally {
      setCompletingOrganizeReview(false);
      organizeReviewBusyRef.current = false;
    }
  }

  function toggleShareDocSelected(documentId: string) {
    setSelectedShareDocIds((current) => {
      const next = new Set(current);
      if (next.has(documentId)) next.delete(documentId);
      else next.add(documentId);
      return next;
    });
  }

  async function createPackage(matter: Matter) {
    if (selectedShareDocIds.size === 0 || isBusy) return; // No mutation when nothing is selected, and no overlapping create on a rapid double-click.
    setError('');
    setIsBusy(true);
    try {
      const documentIds = Array.from(selectedShareDocIds);
      await fenzsafeStore.createAttorneyPackage(matter.id, matter.version, documentIds, crypto.randomUUID());
      setSelectedShareDocIds(new Set());
      setLastStatus(`Attorney package created — share link ready below. ${documentIds.length} document${documentIds.length === 1 ? '' : 's'} shared.`);
      focusStatusSoon();
    } catch (thrown) {
      setError(friendlyMutationError(thrown, 'Could not create the attorney package. Try again.'));
    } finally {
      setIsBusy(false);
    }
  }

  function startUpdatingShare(matter: Matter) {
    setSelectedShareDocIds(new Set(matter.attorneyPackage.sharedDocumentIds ?? []));
    setShowUpdateSharePanel(true);
  }

  async function saveShareUpdate(matter: Matter) {
    if (selectedShareDocIds.size === 0 || isBusy) return;
    setError('');
    setIsBusy(true);
    try {
      const documentIds = Array.from(selectedShareDocIds);
      await fenzsafeStore.updateAttorneyPackageSnapshot(matter.id, matter.version, documentIds, crypto.randomUUID());
      setShowUpdateSharePanel(false);
      setLastStatus(`Shared package updated. ${documentIds.length} document${documentIds.length === 1 ? '' : 's'} shared.`);
      focusStatusSoon();
    } catch (thrown) {
      setError(friendlyMutationError(thrown, 'Could not update the shared package. Try again.'));
    } finally {
      setIsBusy(false);
    }
  }

  async function revokePackage(matter: Matter) {
    if (isBusy) return; // No overlapping revoke on a rapid double-click.
    setError('');
    setIsBusy(true);
    try {
      await fenzsafeStore.revokeAttorneyPackage(matter.id, matter.attorneyPackage.rowVersion ?? matter.version, crypto.randomUUID());
      setLastStatus('Attorney access revoked. The previous share link no longer opens.');
      focusStatusSoon();
    } catch (thrown) {
      setError(friendlyMutationError(thrown, 'Could not revoke access. Try again.'));
    } finally {
      setIsBusy(false);
    }
  }

  async function toggleNotifyOnAttorneyOpen(matter: Matter, notifyOnAttorneyOpen: boolean) {
    if (pendingNotifyRef.current.has(matter.id)) return;
    setError('');
    pendingNotifyRef.current.add(matter.id);
    setPendingNotifyMatterIds((prev) => new Set(prev).add(matter.id));
    try {
      await fenzsafeStore.updateNotificationPreference(matter.id, matter.version, notifyOnAttorneyOpen, crypto.randomUUID());
      setLastStatus(notifyOnAttorneyOpen ? 'Email notification turned on for this matter.' : 'Email notification turned off for this matter.');
    } catch (thrown) {
      setError(friendlyMutationError(thrown, 'Could not update your notification preference. Try again.'));
    } finally {
      pendingNotifyRef.current.delete(matter.id);
      setPendingNotifyMatterIds((prev) => {
        const next = new Set(prev);
        next.delete(matter.id);
        return next;
      });
    }
  }

  function toggleMatterWatchTriggerType(triggerType: MatterWatchTriggerType) {
    setMatterWatchTriggerTypes((current) => (current.includes(triggerType) ? current.filter((item) => item !== triggerType) : [...current, triggerType]));
  }

  async function subscribeMatterWatch(matter: Matter) {
    if (matterWatchBusyRef.current) return;
    const partyNames = matterWatchPartyNames
      .split(',')
      .map((name) => name.trim())
      .filter((name) => name !== '');
    if (partyNames.length === 0 || matterWatchCourt.trim() === '' || matterWatchCounty.trim() === '' || matterWatchTriggerTypes.length === 0) {
      setError('Enter at least one party name, the court, the county, and at least one filing type to watch for.');
      return;
    }
    setError('');
    matterWatchBusyRef.current = true;
    setMatterWatchBusy(true);
    const matterId = matter.id;
    try {
      await fenzsafeStore.subscribeMatterWatch(
        matter.id,
        {partyNames, court: matterWatchCourt.trim(), county: matterWatchCounty.trim(), triggerTypes: matterWatchTriggerTypes},
        crypto.randomUUID(),
      );
      if (activeMatterIdRef.current !== matterId) return;
      setMatterWatchPartyNames('');
      setMatterWatchCourt('');
      setMatterWatchCounty('');
      setMatterWatchTriggerTypes([]);
      setLastStatus('Now watching this matter for new filings.');
      focusStatusSoon();
    } catch (thrown) {
      if (activeMatterIdRef.current === matterId) setError(friendlyMutationError(thrown, 'Matter Watch could not be confirmed. Reload before trying another action.'));
    } finally {
      matterWatchBusyRef.current = false;
      setMatterWatchBusy(false);
    }
  }

  async function toggleMatterWatchEnabled(matter: Matter, enabled: boolean) {
    if (matterWatchBusyRef.current || !matter.matterWatch) return;
    setError('');
    matterWatchBusyRef.current = true;
    setMatterWatchBusy(true);
    const matterId = matter.id;
    try {
      await fenzsafeStore.updateMatterWatch(matter.id, matter.matterWatch.version, {enabled}, crypto.randomUUID());
    } catch (thrown) {
      if (activeMatterIdRef.current === matterId) setError(friendlyMutationError(thrown, 'Matter Watch could not be confirmed. Reload before trying another action.'));
    } finally {
      matterWatchBusyRef.current = false;
      setMatterWatchBusy(false);
    }
  }

  async function unsubscribeMatterWatch(matter: Matter) {
    if (matterWatchBusyRef.current || !matter.matterWatch) return;
    setError('');
    matterWatchBusyRef.current = true;
    setMatterWatchBusy(true);
    const matterId = matter.id;
    try {
      await fenzsafeStore.unsubscribeMatterWatch(matter.id, matter.matterWatch.version, crypto.randomUUID());
      if (activeMatterIdRef.current !== matterId) return;
      setLastStatus('Stopped watching this matter.');
      focusStatusSoon();
    } catch (thrown) {
      if (activeMatterIdRef.current === matterId) setError(friendlyMutationError(thrown, 'Matter Watch could not be confirmed. Reload before trying another action.'));
    } finally {
      matterWatchBusyRef.current = false;
      setMatterWatchBusy(false);
    }
  }

  async function saveRepresentationStatus(matter: Matter) {
    if (!capabilities.representationReady || isBusy) return;
    const matterId = matter.id;
    setError('');
    setIsBusy(true);
    try {
      const updated = await fenzsafeStore.updateRepresentationStatus(matterId, matter.version, editRepresentationStatus, crypto.randomUUID());
      if (activeMatterIdRef.current !== matterId) return;
      setEditRepresentationStatus(updated.representationStatus);
      setLastStatus('Representation status saved. This does not change access or share the matter.');
      focusStatusSoon();
    } catch (thrown) {
      if (activeMatterIdRef.current !== matterId) return;
      try {
        const refreshed = await fenzsafeStore.refreshMatter(matterId);
        if (activeMatterIdRef.current === matterId) setEditRepresentationStatus(refreshed.representationStatus);
      } catch {
        // The original failure remains authoritative; never replay the edit automatically.
      }
      if (activeMatterIdRef.current === matterId) {
        setError(friendlyMutationError(thrown, 'The status was not saved. The latest matter was reloaded when possible; review it and try again.'));
      }
    } finally {
      setIsBusy(false);
    }
  }

  async function upgrade(matter: Matter, tier: MatterTier) {
    try {
      await fenzsafeStore.upgradeTier(matter.id, matter.version, tier, crypto.randomUUID());
      setLastStatus(`Switched to ${TIER_LABEL[tier]}.`);
      focusStatusSoon();
    } catch (thrown) {
      setError(friendlyMutationError(thrown, 'Could not change your plan. Try again.'));
    }
  }

  if (!activeMatter) {
    return (
      <section aria-label="FenzSafe onboarding" className="fsa-root">
        <HolmesJrGuide pose="guide" tip="Start with your first matter — only a name is required. Add a reference if it helps you find the matter later. You can add documents whenever you have them." />
        <p className="fsa-flag" role="status">
          {isRealBackend ? (
            <>
              <strong>Free to start</strong>
              <span>What you enter here is saved to your account. Nothing is billed unless you choose a paid plan.</span>
            </>
          ) : isGuestExperience ? (
            <>
              <strong>Browser-only guest workspace</strong>
              <span>What you enter stays in this browser tab. Nothing is uploaded to a server or billed.</span>
            </>
          ) : (
            <>
              <strong>Local synthetic module</strong>
              <span>Nothing you enter here is uploaded, stored, or billed. This preview lives in your browser only.</span>
            </>
          )}
        </p>
        <div className="fsa-wordmark">
          <span className="fsa-mark" aria-hidden="true">
            F
          </span>
          FENZSAFE
        </div>
        <header className="fsa-header">
          <p className="fsa-eyebrow">LEGAL MATTER ORGANIZER</p>
          <h1>Turn scattered legal files into a package people can review.</h1>
          <p>Organize documents and evidence for one matter, at no cost, then share an attorney-ready package when you're ready.</p>
        </header>

        {error && (
          <p className="fsa-field-error" role="alert">
            {error}
          </p>
        )}
        {/* Issue #697/#747 A2: archiving the only remaining active matter lands here (no
            activeMatter left) -- without this, that success receipt had nowhere to render. */}
        <p ref={statusRef} tabIndex={-1} role="status" className="fsa-package-status">
          {lastStatus}
        </p>

        {state.creationSummary?.available && capabilities.representationReady ? <form className="fsa-panel" onSubmit={createMatter} noValidate>
          <h2>Start your first matter</h2>
          <div className="fsa-field">
            <label htmlFor="fsa-matter-name">Matter name</label>
            <input
              id="fsa-matter-name"
              required
              value={matterName}
              onChange={(event) => {
                setMatterName(event.target.value);
                setNameError('');
              }}
              aria-invalid={nameError ? 'true' : undefined}
              aria-describedby={nameError ? 'fsa-matter-name-error' : undefined}
              placeholder="e.g. Landlord dispute — Cedar Ridge Apartments"
            />
            {nameError && (
              <p id="fsa-matter-name-error" className="fsa-field-error">
                {nameError}
              </p>
            )}
          </div>
          <div className="fsa-field">
            <label htmlFor="fsa-matter-reference">Your reference (optional)</label>
            <input id="fsa-matter-reference" value={matterReference} onChange={(event) => setMatterReference(event.target.value)} placeholder="Any label that helps you find this later" />
          </div>
          <fieldset className="fsa-role-fieldset">
            <legend>What is the representation status for this matter?</legend>
            <label className="fsa-radio-row">
              <input type="radio" name="fsa-representation" checked={newMatterRepresentation === 'represented'} onChange={() => setNewMatterRepresentation('represented')} />
              Represented by an attorney
            </label>
            <label className="fsa-radio-row">
              <input type="radio" name="fsa-representation" checked={newMatterRepresentation === 'seeking_counsel'} onChange={() => setNewMatterRepresentation('seeking_counsel')} />
              Seeking an attorney
            </label>
            <label className="fsa-radio-row">
              <input type="radio" name="fsa-representation" checked={newMatterRepresentation === 'self_represented'} onChange={() => setNewMatterRepresentation('self_represented')} />
              Representing myself
            </label>
            <p className="fsa-package-status">No choice is required. Leaving all three blank saves Not specified. This description never changes access or shares the matter.</p>
            {newMatterRepresentation !== null && <button type="button" className="fsa-secondary" onClick={() => setNewMatterRepresentation(null)}>Clear choice — Not specified</button>}
          </fieldset>
          <div className="fsa-actions">
            <button type="submit" className="fsa-primary" disabled={isBusy}>
              {isBusy ? 'Creating…' : 'Create my matter'}
            </button>
          </div>
        </form> : (
          <section className="fsa-panel" aria-labelledby="fsa-new-matter-unavailable-heading">
            <h2 id="fsa-new-matter-unavailable-heading">Start a new matter</h2>
            <p>
              {state.creationSummary?.reason === 'included_matter_used'
                ? `The Free Organizer includes ${state.creationSummary.limit} ${state.creationSummary.limit === 1 ? 'matter' : 'matters'}. Additional-matter upgrades aren’t available yet. Your existing matters remain available.`
                : !capabilities.representationReady
                  ? 'New matter setup is temporarily unavailable while representation details are being prepared. Your existing matters remain available.'
                  : 'New matter creation is temporarily unavailable. Your existing matters remain available.'}
            </p>
          </section>
        )}

        <div className="fsa-panel">
          <h2>What the free organizer includes</h2>
          <ul className="fsa-free-tier-note">
            <li>Create one matter and upload the documents you choose</li>
            <li>Organize by category, rename, and mark status as you review</li>
            <li>A simple timeline and index of everything in the matter</li>
            <li>A branded attorney-ready cover sheet and shareable link</li>
            <li>See whether the attorney has opened what you shared</li>
          </ul>
        </div>
        {archivedSection}
      </section>
    );
  }

  const counts = triageCounts(activeMatter);
  const otherMatters = state.matters.filter((matter) => matter.id !== activeMatter.id && !matter.archived);
  const needsReviewDocs = activeMatter.documents.filter((doc) => doc.evidenceRelevance === 'needs_review');
  const phoneEvidenceGroups = groupPhoneEvidenceBySource(activeMatter);
  const readinessFlags = matterReadinessFlags(activeMatter);
  const packetPreviewGroups = groupDocumentsByCategory(activeMatter);
  const shareEligibleDocuments = activeMatter.documents.filter((doc) => doc.processingStatus === 'accepted' && !doc.supersededBy);
  const pendingEvidenceRequests = activeMatter.evidenceRequests.filter((evidenceRequest) => evidenceRequest.status === 'pending');
  // Same eligibility as attorney-package sharing -- a document can only fulfil a request once it
  // is actually accepted and current, not a superseded earlier version.
  const eligibleDocumentsForRequest = shareEligibleDocuments;
  const visibleOrganizeSuggestions = activeMatter.organizeSuggestions.filter((suggestion) => !skippedSuggestionIds.has(suggestion.documentId) && !reviewedSuggestionIds.has(suggestion.documentId));
  const hasUnsavedOrganizeReview = organizeReviewCounts.accepted > 0 || organizeReviewCounts.edited > 0 || organizeReviewCounts.skipped > 0;

  return (
    <section aria-label="FenzSafe matter workspace" className="fsa-root">
      <HolmesJrGuide
        pose="guide"
        tip={holmesJrWorkspaceTip({
          jurisdictionEscalation,
          retryableChunkedSessionCount: activeMatter.documents.filter((doc) => retryableChunkedSessions.has(doc.id)).length,
          pendingEvidenceRequestCount: pendingEvidenceRequests.length,
          organizeSuggestionCount: visibleOrganizeSuggestions.length,
          topReadinessFlagMessage: readinessFlags.length > 0 ? readinessFlags[0].message : null,
          activeChunkedUploadCount: activeMatter.documents.filter((doc) => chunkedUploadProgress.has(doc.id)).length,
        })}
      />
      <p className="fsa-flag" role="status">
        {isRealBackend ? (
          <>
            <strong>Saved to your account</strong>
            <span>Documents and matters below are stored for you. Uploaded files are scanned before they become available.</span>
          </>
        ) : isGuestExperience ? (
          <>
            <strong>Browser-only guest workspace</strong>
            <span>Your matter and document list stay in this browser tab. This guest workspace does not upload files to a server.</span>
          </>
        ) : (
          <>
            <strong>Local synthetic module</strong>
            <span>Every document and matter below is fixture data held in this browser only.</span>
          </>
        )}
      </p>
      <div className="fsa-wordmark">
        <span className="fsa-mark" aria-hidden="true">
          F
        </span>
        FENZSAFE
      </div>

      {capabilities.evidenceRequestsReady && pendingEvidenceRequests.length > 0 && (
        <section className="fsa-panel fsa-evidence-requests" aria-labelledby="fsa-evidence-requests-heading">
          <h2 id="fsa-evidence-requests-heading">Your attorney is asking for {pendingEvidenceRequests.length === 1 ? 'something' : `${pendingEvidenceRequests.length} things`}</h2>
          <ul className="fsa-evidence-request-list">
            {pendingEvidenceRequests.map((evidenceRequest) => (
              <li key={evidenceRequest.id} className="fsa-evidence-request">
                <p className="fsa-evidence-request-description">{evidenceRequest.description}</p>
                <div className="fsa-evidence-request-actions">
                  <select
                    aria-label={`Choose a document for: ${evidenceRequest.description}`}
                    value={evidenceRequestSelections[evidenceRequest.id] ?? ''}
                    onChange={(event) => setEvidenceRequestSelections((current) => ({...current, [evidenceRequest.id]: event.target.value}))}
                    disabled={resolvingRequestId === evidenceRequest.id}
                  >
                    <option value="">Choose a document you already added…</option>
                    {eligibleDocumentsForRequest.map((doc) => (
                      <option key={doc.id} value={doc.id}>
                        {doc.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="fsa-primary"
                    disabled={!evidenceRequestSelections[evidenceRequest.id] || resolvingRequestId === evidenceRequest.id}
                    onClick={() => fulfillEvidenceRequest(activeMatter, evidenceRequest.id, evidenceRequest.version)}
                  >
                    {resolvingRequestId === evidenceRequest.id ? 'Sending…' : 'Provide this'}
                  </button>
                  <button
                    type="button"
                    className="fsa-secondary"
                    disabled={resolvingRequestId === evidenceRequest.id}
                    onClick={() => declineEvidenceRequest(activeMatter, evidenceRequest.id, evidenceRequest.version)}
                  >
                    I don&rsquo;t have this
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {capabilities.documentsReady && (visibleOrganizeSuggestions.length > 0 || hasUnsavedOrganizeReview) && (
        <section className="fsa-panel fsa-organize-suggestions" aria-labelledby="fsa-organize-suggestions-heading">
          <h2 id="fsa-organize-suggestions-heading">Organize my documents</h2>
          <p>
            These are filename-based suggestions only -- FenzSafe hasn&rsquo;t read the contents of any document. Nothing changes unless you accept or choose a different category yourself.
          </p>
          {visibleOrganizeSuggestions.length > 0 && (
            <ul className="fsa-organize-suggestion-list">
              {visibleOrganizeSuggestions.map((suggestion) => {
                const document = activeMatter.documents.find((item) => item.id === suggestion.documentId);
                const isApplying = applyingSuggestionId === suggestion.documentId;
                return (
                  <li key={suggestion.documentId} className="fsa-organize-suggestion">
                    <p className="fsa-organize-suggestion-description">
                      {document ? <strong>{document.name}</strong> : 'A document'}: currently <em>{suggestion.currentCategory}</em>, might be <em>{suggestion.suggestedCategory}</em>. {suggestion.reason}
                    </p>
                    <div className="fsa-organize-suggestion-actions">
                      <button type="button" className="fsa-primary" disabled={isApplying || completingOrganizeReview} onClick={() => acceptOrganizeSuggestion(activeMatter, suggestion)}>
                        {isApplying ? 'Saving…' : `Move to ${suggestion.suggestedCategory}`}
                      </button>
                      <label className="fsa-inline-label" htmlFor={`fsa-organize-edit-${suggestion.documentId}`}>
                        Choose a different category for {document?.name ?? 'this document'}
                      </label>
                      <select
                        id={`fsa-organize-edit-${suggestion.documentId}`}
                        value={organizeEditSelections[suggestion.documentId] ?? ''}
                        onChange={(event) => setOrganizeEditSelections((current) => ({...current, [suggestion.documentId]: event.target.value as DocumentCategory}))}
                        disabled={isApplying || completingOrganizeReview}
                      >
                        <option value="">Choose a different category…</option>
                        {DOCUMENT_CATEGORIES.map((category) => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="fsa-secondary"
                        disabled={!organizeEditSelections[suggestion.documentId] || isApplying || completingOrganizeReview}
                        onClick={() => applyOrganizeSuggestionEdit(activeMatter, suggestion)}
                      >
                        Move there instead
                      </button>
                      <button type="button" className="fsa-secondary" disabled={applyingSuggestionId !== null || completingOrganizeReview} onClick={() => skipOrganizeSuggestion(suggestion.documentId)}>
                        Skip
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <button type="button" className="fsa-secondary" disabled={completingOrganizeReview || applyingSuggestionId !== null} onClick={() => completeOrganizeReview(activeMatter)}>
            {completingOrganizeReview ? 'Saving…' : 'Done reviewing'}
          </button>
        </section>
      )}

      {pendingTextMessageHandoff && (
        <section className="fsa-panel fsa-text-message-handoff" aria-labelledby="fsa-text-message-handoff-heading">
          <h2 id="fsa-text-message-handoff-heading">Add your formatted text-message record?</h2>
          <p>
            You formatted a text-message conversation on FenzSafe&rsquo;s free print tool
            {pendingTextMessageHandoff.caseName ? ` for ${pendingTextMessageHandoff.caseName}` : ''}. Add it to{' '}
            <strong>{activeMatter.name}</strong> as evidence, or discard it — nothing has been uploaded yet.
          </p>
          <div className="fsa-text-message-handoff-actions">
            <button type="button" className="fsa-primary" disabled={isBusy} onClick={() => addTextMessageHandoffDocument(activeMatter)}>
              {isBusy ? 'Adding…' : 'Add to this matter'}
            </button>
            <button type="button" className="fsa-secondary" disabled={isBusy} onClick={discardTextMessageHandoff}>
              Discard
            </button>
          </div>
        </section>
      )}

      <header className="fsa-header">
        <p className="fsa-eyebrow">MATTER</p>
        {isEditingMatter ? (
          <div className="fsa-edit-matter">
            <div className="fsa-field">
              <label htmlFor="fsa-edit-matter-name">Matter name</label>
              <input
                id="fsa-edit-matter-name"
                value={editMatterName}
                onChange={(event) => {
                  setEditMatterName(event.target.value);
                  setEditMatterNameError('');
                }}
                aria-invalid={editMatterNameError ? 'true' : undefined}
                aria-describedby={editMatterNameError ? 'fsa-edit-matter-name-error' : undefined}
              />
              {editMatterNameError && (
                <p id="fsa-edit-matter-name-error" className="fsa-field-error">
                  {editMatterNameError}
                </p>
              )}
            </div>
            <div className="fsa-field">
              <label htmlFor="fsa-edit-matter-reference">Your reference</label>
              <input id="fsa-edit-matter-reference" value={editMatterReference} onChange={(event) => setEditMatterReference(event.target.value)} />
            </div>
            <div className="fsa-actions">
              <button type="button" className="fsa-secondary" onClick={() => setEditingMatterId(null)} disabled={isBusy}>
                Cancel
              </button>
              <button type="button" className="fsa-primary" onClick={() => saveMatterEdits(activeMatter)} disabled={isBusy}>
                {isBusy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <h1 ref={headingRef} tabIndex={-1}>
              {activeMatter.name}
            </h1>
            <p>
              Reference {activeMatter.reference} · Created {friendlyTimestamp(activeMatter.createdAt)} · <span className={`fsa-tier-badge is-${activeMatter.tier}`}>{TIER_LABEL[activeMatter.tier]}</span>
            </p>
            <p className="fsa-crosslink">
              <button type="button" onClick={() => startEditingMatter(activeMatter)}>
                Edit name/reference
              </button>
              {otherMatters.map((matter) => (
                <button
                  key={matter.id}
                  type="button"
                  onClick={() => {
                    setActiveMatterId(matter.id);
                    requestAnimationFrame(() => headingRef.current?.focus());
                  }}
                >
                  Switch to {matter.name} →
                </button>
              ))}
              <button type="button" onClick={() => archiveMatter(activeMatter)} disabled={isBusy}>
                Archive this matter
              </button>
            </p>
          </>
        )}
        <div className="fsa-actions">
          <button ref={newMatterTriggerRef} type="button" className="fsa-secondary" aria-expanded={showNewMatterFlow} aria-controls="fsa-new-matter-flow" onClick={() => setShowNewMatterFlow(true)}>
            Start a new matter
          </button>
        </div>
      </header>

      <section className="fsa-panel" aria-labelledby="fsa-representation-heading">
        <h2 id="fsa-representation-heading">Representation status</h2>
        <p>{activeMatter.representationStatus === 'represented' ? 'Represented by an attorney' : activeMatter.representationStatus === 'seeking_counsel' ? 'Seeking an attorney' : activeMatter.representationStatus === 'self_represented' ? 'Representing myself' : 'Not specified'}</p>
        <p className="fsa-package-status">This describes this matter only. It never grants attorney access, changes permissions, or shares evidence. Plain-language help remains available for every status.</p>
        {capabilities.representationReady ? (
          <div className="fsa-field">
            <label htmlFor="fsa-edit-representation">Change representation status</label>
            <select id="fsa-edit-representation" value={editRepresentationStatus ?? ''} onChange={(event) => setEditRepresentationStatus(event.target.value === '' ? null : event.target.value as FenzSafeRepresentationStatus)} disabled={isBusy}>
              <option value="">Not specified</option>
              <option value="represented">Represented by an attorney</option>
              <option value="seeking_counsel">Seeking an attorney</option>
              <option value="self_represented">Representing myself</option>
            </select>
            <div className="fsa-actions">
              <button type="button" className="fsa-secondary" disabled={isBusy || editRepresentationStatus === activeMatter.representationStatus} onClick={() => saveRepresentationStatus(activeMatter)}>
                {isBusy ? 'Saving…' : 'Save representation status'}
              </button>
            </div>
          </div>
        ) : (
          <p className="fsa-package-status" role="status">Editing this detail will be available after the representation update is ready. You can keep using this matter now.</p>
        )}
      </section>

      {showNewMatterFlow && (
        <section id="fsa-new-matter-flow" className="fsa-panel" aria-labelledby="fsa-new-matter-heading">
          <h2 id="fsa-new-matter-heading" ref={newMatterHeadingRef} tabIndex={-1}>Start a new matter</h2>
          {state.creationSummary?.available && capabilities.representationReady ? (
            <form onSubmit={createMatter} noValidate>
              <div className="fsa-field">
                <label htmlFor="fsa-new-matter-name">Matter name</label>
                <input
                  id="fsa-new-matter-name"
                  required
                  value={matterName}
                  onChange={(event) => {
                    setMatterName(event.target.value);
                    setNameError('');
                  }}
                  aria-invalid={nameError ? 'true' : undefined}
                  aria-describedby={nameError ? 'fsa-new-matter-name-error' : undefined}
                />
                {nameError && <p id="fsa-new-matter-name-error" className="fsa-field-error">{nameError}</p>}
              </div>
              <div className="fsa-field">
                <label htmlFor="fsa-new-matter-reference">Your reference (optional)</label>
                <input id="fsa-new-matter-reference" value={matterReference} onChange={(event) => setMatterReference(event.target.value)} />
              </div>
              <fieldset className="fsa-role-fieldset">
                <legend>What is the representation status for this matter?</legend>
                <label className="fsa-radio-row">
                  <input type="radio" name="fsa-new-representation" checked={newMatterRepresentation === 'represented'} onChange={() => setNewMatterRepresentation('represented')} />
                  Represented by an attorney
                </label>
                <label className="fsa-radio-row">
                  <input type="radio" name="fsa-new-representation" checked={newMatterRepresentation === 'seeking_counsel'} onChange={() => setNewMatterRepresentation('seeking_counsel')} />
                  Seeking an attorney
                </label>
                <label className="fsa-radio-row">
                  <input type="radio" name="fsa-new-representation" checked={newMatterRepresentation === 'self_represented'} onChange={() => setNewMatterRepresentation('self_represented')} />
                  Representing myself
                </label>
                <p className="fsa-package-status">No choice is required. Leaving all three blank saves Not specified. This description never changes access or shares the matter.</p>
                {newMatterRepresentation !== null && <button type="button" className="fsa-secondary" onClick={() => setNewMatterRepresentation(null)}>Clear choice — Not specified</button>}
              </fieldset>
              <div className="fsa-actions">
                <button type="button" className="fsa-secondary" onClick={closeNewMatterFlow} disabled={isBusy}>Cancel</button>
                <button type="submit" className="fsa-primary" disabled={isBusy}>{isBusy ? 'Creating…' : 'Create matter'}</button>
              </div>
            </form>
          ) : (
            <>
              <p>
                {state.creationSummary?.reason === 'included_matter_used'
                  ? `The Free Organizer includes ${state.creationSummary.limit} ${state.creationSummary.limit === 1 ? 'matter' : 'matters'}. Additional-matter upgrades aren’t available yet. Your existing matters remain available.`
                  : !capabilities.representationReady
                    ? 'New matter setup is temporarily unavailable while representation details are being prepared. Your existing matters remain available.'
                    : 'New matter creation is temporarily unavailable. Your existing matters remain available.'}
              </p>
              <button type="button" className="fsa-secondary" onClick={closeNewMatterFlow}>Keep working in this matter</button>
            </>
          )}
        </section>
      )}

      {error && (
        <p className="fsa-field-error" role="alert">
          {error}
        </p>
      )}
      {downloadStatus}
      <p ref={statusRef} tabIndex={-1} role="status" className="fsa-package-status">
        {lastStatus}
      </p>

      <div className="fsa-stats">
        <div className="fsa-stat">
          <div className="fsa-stat-num">{counts.total}</div>
          <div className="fsa-stat-label">Documents</div>
        </div>
        <div className="fsa-stat">
          <div className="fsa-stat-num">{counts.unsorted}</div>
          <div className="fsa-stat-label">Unsorted</div>
        </div>
        <div className="fsa-stat">
          <div className="fsa-stat-num">{counts.filed}</div>
          <div className="fsa-stat-label">Filed</div>
        </div>
        <div className="fsa-stat">
          <div className="fsa-stat-num">{counts.needsRelevanceReview}</div>
          <div className="fsa-stat-label">Needs review</div>
        </div>
      </div>

      <section className="fsa-panel" aria-labelledby="fsa-organizer-heading">
        <h2 id="fsa-organizer-heading">Organizer</h2>
        {!capabilities.documentsReady && (
          <p className="fsa-package-status" role="status">
            Document storage isn't finished being set up for this environment yet — you can still create and organize matters. Adding documents will appear here once it's ready.
          </p>
        )}
        {capabilities.documentsReady && (
          <>
        <FenzSafeEmailImport matterId={activeMatter.id} matterName={activeMatter.name} maxBytes={FENZSAFE_DOCUMENT_MAX_BYTES} disabled={isBusy} onImport={file=>importEmailFile(activeMatter,file)}/>
        <div className="fsa-add-doc">
          <div className="fsa-field">
            <label htmlFor="fsa-new-doc-category">Category for the next document you add</label>
            <select id="fsa-new-doc-category" value={newDocCategory} onChange={(event) => setNewDocCategory(event.target.value as DocumentCategory)}>
              {DOCUMENT_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
          <button type="button" className="fsa-primary" onClick={() => fileInputRef.current?.click()} disabled={isBusy}>
            Choose a file
          </button>
          <input ref={fileInputRef} type="file" hidden onChange={(event) => handleFileSelected(activeMatter, event)} />
          <input
            ref={attachBytesFileInputRef}
            type="file"
            hidden
            onChange={(event) => {
              const docId = attachBytesFileInputRef.current?.dataset.forDoc;
              if (docId) attachBytes(activeMatter, docId, event);
            }}
          />
        </div>

          <details className="fsa-category-help" open>
            <summary>Holmes Jr. — not sure what these categories mean?</summary>
            <dl>
              {DOCUMENT_CATEGORIES.map((category) => (
                <div key={category}>
                  <dt>{category}</dt>
                  <dd>{CATEGORY_HELP[category]}</dd>
                </div>
              ))}
            </dl>
          </details>

        <div className="fsa-add-doc">
          <div className="fsa-field">
            <label htmlFor="fsa-new-doc-name">Or add by name only (no file is read)</label>
            <input id="fsa-new-doc-name" value={newDocName} onChange={(event) => setNewDocName(event.target.value)} placeholder="e.g. eviction-notice.pdf" />
          </div>
          <button type="button" className="fsa-secondary" onClick={() => addDocument(activeMatter)} disabled={isBusy || !newDocName.trim()}>
            Add document
          </button>
        </div>
          </>
        )}

        {capabilities.documentsReady && activeMatter.documents.length === 0 && <p className="fsa-package-status">No documents yet. Choose a file above, or add one by name to try the organizer.</p>}

        <div className="fsa-table-wrap" hidden={activeMatter.documents.length === 0}>
          <table className="fsa-table">
            <thead>
              <tr>
                <th>Document</th>
                <th>Category</th>
                <th>Status</th>
                <th>Relevance</th>
              </tr>
            </thead>
            <tbody>
              {activeMatter.documents.map((doc) => {
                const isExpanded = expandedDocId === doc.id;
                const supersedingDoc = doc.supersededBy ? activeMatter.documents.find((item) => item.id === doc.supersededBy) : undefined;
                const previousVersionDoc = doc.replacesDocumentId ? activeMatter.documents.find((item) => item.id === doc.replacesDocumentId) : undefined;
                const hasNoFile = doc.processingStatus === 'awaiting_upload';
                const sourceSummary = uploadingDocIds.has(doc.id)
                  ? isGuestExperience ? 'Adding file in this browser' : 'File selected for upload'
                  : hasNoFile
                    ? doc.source === 'phone_import' ? 'Phone item noted — no file uploaded' : 'Added by name — no file uploaded'
                    : doc.source === 'phone_import'
                      ? `From phone export · ${friendlyTimestamp(doc.addedAt)}`
                      : doc.source === 'desktop_discovery'
                        ? `Found on computer · ${friendlyTimestamp(doc.addedAt)}`
                        : isGuestExperience
                          ? `Added in this browser · ${friendlyTimestamp(doc.addedAt)}`
                          : `Uploaded · ${friendlyTimestamp(doc.addedAt)}`;
                return (
                <Fragment key={doc.id}>
                <tr>
                  <td>
                    <div className="fsa-doc-name-cell">
                      <span>
                        {doc.name}
                        {doc.docVersion > 1 && <span className="fsa-doc-version"> · v{doc.docVersion}</span>}
                      </span>
                      <span className="fsa-doc-source">
                        {sourceSummary}
                      </span>
                      {doc.supersededBy && <span className="fsa-badge is-superseded">Replaced</span>}
                      <button
                        type="button"
                        className="fsa-link-button"
                        onClick={() => setExpandedDocId(isExpanded ? null : doc.id)}
                        aria-expanded={isExpanded}
                        aria-label={`${isExpanded ? 'Hide' : 'View'} details for ${doc.name}`}
                      >
                        {isExpanded ? 'Hide details' : 'View details'}
                      </button>
                    </div>
                  </td>
                  <td>
                    <label className="fsa-inline-label" htmlFor={`fsa-cat-${doc.id}`}>
                      Category for {doc.name}
                    </label>
                    <select id={`fsa-cat-${doc.id}`} value={doc.category} disabled={pendingFieldEditDocIds.has(doc.id)} onChange={(event) => updateCategory(activeMatter, doc.id, event.target.value as DocumentCategory)}>
                      {DOCUMENT_CATEGORIES.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {uploadingDocIds.has(doc.id) ? (
                      chunkedUploadProgress.has(doc.id) ? (
                        <div className="fsa-upload-progress">
                          <span className="fsa-badge is-uploading">
                            Uploading… {formatChunkedUploadPercent(chunkedUploadProgress.get(doc.id)!)}
                          </span>
                          <button type="button" className="fsa-link-button" onClick={() => cancelChunkedUpload(doc.id)}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <span className="fsa-badge is-uploading">Uploading…</span>
                      )
                    ) : doc.processingStatus === 'accepted' ? (
                      <>
                        <label className="fsa-inline-label" htmlFor={`fsa-status-${doc.id}`}>
                          Status for {doc.name}
                        </label>
                        <select id={`fsa-status-${doc.id}`} value={doc.triageState} disabled={pendingFieldEditDocIds.has(doc.id)} onChange={(event) => updateTriage(activeMatter, doc.id, event.target.value as TriageState)}>
                          {TRIAGE_STATES.map((triageState) => (
                            <option key={triageState} value={triageState}>
                              {triageState}
                            </option>
                          ))}
                        </select>
                      </>
                    ) : (
                      <span className={`fsa-badge is-${doc.processingStatus}`}>{PROCESSING_LABEL[doc.processingStatus]}</span>
                    )}
                  </td>
                  <td>
                    {doc.evidenceRelevance ? (
                      doc.evidenceRelevance === 'needs_review' ? (
                        <div className="fsa-actions">
                          <button type="button" className="fsa-secondary" disabled={pendingFieldEditDocIds.has(doc.id)} onClick={() => markRelevance(activeMatter, doc.id, 'relevant')}>
                            Relevant
                          </button>
                          <button type="button" className="fsa-secondary" disabled={pendingFieldEditDocIds.has(doc.id)} onClick={() => markRelevance(activeMatter, doc.id, 'not_relevant')}>
                            Not relevant
                          </button>
                        </div>
                      ) : (
                        <span className={`fsa-badge is-${doc.evidenceRelevance}`}>{RELEVANCE_LABEL[doc.evidenceRelevance]}</span>
                      )
                    ) : (
                      <span aria-hidden="true">—</span>
                    )}
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="fsa-detail-row">
                    <td colSpan={4}>
                      <dl className="fsa-doc-detail">
                        <div>
                          <dt>Source</dt>
                          <dd>{hasNoFile
                            ? doc.source === 'phone_import' ? 'Phone item noted — no file attached' : 'Name only — no file attached'
                            : `${doc.source === 'phone_import' ? 'Phone export' : 'Direct upload'} · ${doc.sizeKb > 0 ? `${doc.sizeKb.toLocaleString()} KB` : 'size not recorded'}`}</dd>
                        </div>
                        <div>
                          <dt>Added</dt>
                          <dd>{friendlyTimestamp(doc.addedAt)}</dd>
                        </div>
                        <div>
                          <dt>Version</dt>
                          <dd>
                            {previousVersionDoc ? (
                              <>
                                Version {doc.docVersion} of {previousVersionDoc.name}{hasNoFile ? ' (name only)' : ''}{' — '}
                                <button type="button" className="fsa-link-button" onClick={() => setExpandedDocId(previousVersionDoc.id)}>
                                  view previous version
                                </button>
                              </>
                            ) : (
                              hasNoFile ? 'Version 1 (name only)' : 'Version 1 (original)'
                            )}
                          </dd>
                        </div>
                        {supersedingDoc && (
                          <div>
                            <dt>Replaced by</dt>
                            <dd>
                              {supersedingDoc.name}{' '}
                              <button type="button" className="fsa-link-button" onClick={() => setExpandedDocId(supersedingDoc.id)}>
                                view newer version
                              </button>
                            </dd>
                          </div>
                        )}
                      </dl>

                      <div className="fsa-doc-history">
                        {(() => {
                          const chain = documentVersionChain(activeMatter, doc.id);
                          return (
                            <>
                              <p className="fsa-doc-history-title">{chain.some((versionDoc) => versionDoc.processingStatus === 'awaiting_upload') ? 'Document history' : 'Verified document history'}</p>
                              <ol className="fsa-doc-history-list">
                              {chain.map((versionDoc, index) => (
                                <li key={versionDoc.id} className={versionDoc.id === doc.id ? 'is-current' : undefined}>
                                  <span className="fsa-doc-history-version">v{versionDoc.docVersion}</span>
                                  <span>{versionDoc.name}</span>
                                  <span className="fsa-doc-source">
                                    Added {friendlyTimestamp(versionDoc.addedAt)} · {versionDoc.processingStatus === 'awaiting_upload' ? 'name-only entry; no file attached' : index === chain.length - 1 ? 'current version' : 'replaced, kept on file'}
                                  </span>
                                  <span className={`fsa-badge ${versionDoc.processingStatus === 'awaiting_upload' ? '' : `is-provenance-${provenanceKindOf(versionDoc)}`}`}>
                                    {versionDoc.processingStatus === 'awaiting_upload' ? 'No file attached' : <><span aria-hidden="true">{provenanceGlyph(provenanceKindOf(versionDoc))}</span> {provenanceLabel(provenanceKindOf(versionDoc))}</>}
                                  </span>
                                  <span className={`fsa-badge ${versionDoc.processingStatus === 'awaiting_upload' ? '' : `is-verification-${verificationStatusOf(versionDoc)}`}`}>
                                    {versionDoc.processingStatus === 'awaiting_upload' ? 'No file to verify' : <><span aria-hidden="true">{verificationStatusGlyph(verificationStatusOf(versionDoc))}</span> {verificationStatusLabel(verificationStatusOf(versionDoc))}</>}
                                  </span>
                                </li>
                              ))}
                              </ol>
                            </>
                          );
                        })()}
                        <details className="fsa-doc-history-technical">
                          <summary>Technical detail</summary>
                          <dl>
                            <div>
                              <dt>Document ID</dt>
                              <dd>{doc.id}</dd>
                            </div>
                            <div>
                              <dt>Processing status</dt>
                              <dd>{doc.processingStatus}</dd>
                            </div>
                            <div>
                              <dt>Provenance</dt>
                              <dd>{hasNoFile ? 'No file attached' : provenanceLabel(provenanceKindOf(doc))}</dd>
                            </div>
                            <div>
                              <dt>Verification status</dt>
                              <dd>{hasNoFile ? 'No file to verify' : verificationStatusLabel(verificationStatusOf(doc))}</dd>
                            </div>
                            {doc.contentSha256 && (
                              <div>
                                <dt>SHA-256</dt>
                                <dd className="fsa-doc-hash">{doc.contentSha256}</dd>
                              </div>
                            )}
                          </dl>
                          <p className="fsa-doc-history-note">
                            {hasNoFile
                              ? 'No file bytes are attached to this entry. Attach a file before provenance or a hash can be verified.'
                              : doc.contentSha256
                              ? 'This hash was computed by the server when the file was stored and accepted, and is not re-derived by this preview.'
                              : 'No cryptographic hash is computed or verified in this preview. A real integration attaches a verified hash to each accepted version above — that\'s what "verified" means once this is wired to a real backend.'}
                          </p>
                        </details>
                      </div>

                      {!doc.supersededBy && doc.processingStatus === 'accepted' && (
                        <div className="fsa-actions">
                          <FenzSafeViewDocumentButton key={`${activeMatter.id}:${doc.id}:${doc.docVersion}`} name={doc.name} className="fsa-primary" disabled={isBusy}
                            loadFile={signal => fenzsafeStore.downloadDocumentBytes(doc.id, signal)} />
                          <button type="button" className="fsa-secondary" onClick={() => downloadDocument(doc.id, doc.name)} disabled={isBusy} aria-label={`Download ${doc.name}`}>
                            Download
                          </button>
                          {capabilities.matterSummaryReady === true && isFenzSafeAdapterReal() && accountScope !== undefined && (
                            <FenzSafeSummaryDraftControl key={`${accountScope}:${activeMatter.id}:${doc.id}:${doc.docVersion}:${doc.contentSha256 ?? ''}`}
                              scope={accountScope} enabled={capabilities.matterSummaryReady === true} matterId={activeMatter.id} documentId={doc.id}
                              documentName={doc.name} docVersion={doc.docVersion} contentSha256={doc.contentSha256} disabled={isBusy} />
                          )}
                          <button type="button" className="fsa-secondary" onClick={() => triggerVersionUpload(doc.id)} disabled={isBusy}>
                            Upload a new version
                          </button>
                          {confirmRemoveDocId !== doc.id && (
                            <button type="button" className="fsa-secondary fsa-danger" onClick={() => setConfirmRemoveDocId(doc.id)}>
                              Remove document
                            </button>
                          )}
                        </div>
                      )}
                      {!doc.supersededBy && doc.processingStatus === 'awaiting_upload' && !uploadingDocIds.has(doc.id) && (
                        <div className="fsa-actions">
                          {retryableChunkedSessions.has(doc.id) ? (
                            <>
                              <p className="fsa-package-status">The file finished uploading but the last step didn't complete. Finish it without choosing the file again.</p>
                              <button type="button" className="fsa-primary" onClick={() => retryChunkedUpload(activeMatter, doc.id)} disabled={isBusy}>
                                Finish upload
                              </button>
                            </>
                          ) : (
                            <>
                              <p className="fsa-package-status">This document has no file attached yet, so it can't be shared or marked ready.</p>
                              <button type="button" className="fsa-primary" onClick={() => triggerAttachBytes(doc.id)} disabled={isBusy}>
                                Attach a file
                              </button>
                            </>
                          )}
                          {confirmRemoveDocId !== doc.id && (
                            <button type="button" className="fsa-secondary fsa-danger" onClick={() => setConfirmRemoveDocId(doc.id)}>
                              Remove document
                            </button>
                          )}
                        </div>
                      )}
                      {!doc.supersededBy && (doc.processingStatus === 'pending_scan' || doc.processingStatus === 'quarantined' || doc.processingStatus === 'unavailable') && !uploadingDocIds.has(doc.id) && (
                        <div className="fsa-actions">
                          <p className="fsa-package-status" role="status">
                            {STUCK_DOCUMENT_EXPLANATION[doc.processingStatus]}
                          </p>
                          <button type="button" className="fsa-primary" onClick={() => triggerVersionUpload(doc.id)} disabled={isBusy}>
                            Upload a new version
                          </button>
                          {confirmRemoveDocId !== doc.id && (
                            <button type="button" className="fsa-secondary fsa-danger" onClick={() => setConfirmRemoveDocId(doc.id)}>
                              Remove document
                            </button>
                          )}
                        </div>
                      )}
                      {!doc.supersededBy && confirmRemoveDocId === doc.id && (
                        <div className="fsa-confirm-remove" role="alertdialog" aria-label={`Remove ${doc.name}?`}>
                          <p>
                            Remove <strong>{doc.name}</strong> from this matter? This can't be undone.
                          </p>
                          <div className="fsa-actions">
                            <button type="button" className="fsa-secondary" onClick={() => setConfirmRemoveDocId(null)}>
                              Cancel
                            </button>
                            <button type="button" className="fsa-danger-confirm" onClick={() => removeDocument(activeMatter, doc.id, doc.name)}>
                              Remove document
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
                </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <input
          ref={versionFileInputRef}
          type="file"
          hidden
          onChange={(event) => {
            const docId = versionFileInputRef.current?.dataset.forDoc;
            if (docId) addVersion(activeMatter, docId, event);
          }}
        />

        <div className="fsa-actions">
          <button type="button" className="fsa-secondary" onClick={() => downloadCsv(activeMatter, isRealBackend)} disabled={activeMatter.documents.length === 0}>
            Export document index (CSV)
          </button>
        </div>
      </section>

      <FenzSafeDiscoveryPanel matter={activeMatter} />

      <section className="fsa-panel" aria-labelledby="fsa-phone-heading">
        <h2 id="fsa-phone-heading">Phone &amp; message evidence</h2>
        <p className="fsa-package-status">
          Bring in items from a phone backup or export — message threads, photos, videos, or call logs. Only the source, label, and type you give each item are recorded; nothing is read from an
          actual device or file in this preview.
        </p>
        <div className="fsa-add-doc">
          <div className="fsa-field">
            <label htmlFor="fsa-phone-source">Device or export source</label>
            <input id="fsa-phone-source" value={phoneSourceLabel} onChange={(event) => setPhoneSourceLabel(event.target.value)} placeholder="e.g. iPhone 14 — Messages export, Sept 2026" />
          </div>
          <div className="fsa-field">
            <label htmlFor="fsa-phone-label">What's in this item?</label>
            <input id="fsa-phone-label" value={phoneItemLabel} onChange={(event) => setPhoneItemLabel(event.target.value)} placeholder="e.g. Texts with the landlord, Jan–Mar" />
          </div>
          <div className="fsa-field">
            <label htmlFor="fsa-phone-type">Type</label>
            <select id="fsa-phone-type" value={phoneItemType} onChange={(event) => setPhoneItemType(event.target.value as PhoneItemType)}>
              {(Object.keys(PHONE_ITEM_TYPE_LABEL) as PhoneItemType[]).map((type) => (
                <option key={type} value={type}>
                  {PHONE_ITEM_TYPE_LABEL[type]}
                </option>
              ))}
            </select>
          </div>
          <button type="button" className="fsa-secondary" onClick={() => importPhoneItem(activeMatter)} disabled={isBusy || !phoneItemLabel.trim() || !phoneSourceLabel.trim()}>
            Import this item
          </button>
        </div>

        {phoneEvidenceGroups.length === 0 ? (
          <p className="fsa-package-status">No phone evidence imported yet.</p>
        ) : (
          <div className="fsa-phone-groups">
            {phoneEvidenceGroups.map((group) => (
              <div key={group.source} className="fsa-phone-group">
                <p className="fsa-phone-group-source">
                  {group.source} <span className="fsa-doc-source">· {group.documents.length} item{group.documents.length === 1 ? '' : 's'}</span>
                </p>
                <ul className="fsa-phone-group-list">
                  {group.documents.map((doc) => (
                    <li key={doc.id}>
                      <span>{doc.name}</span>
                      <span className={`fsa-badge is-${doc.evidenceRelevance}`}>{doc.evidenceRelevance === 'needs_review' ? 'Needs review' : RELEVANCE_LABEL[doc.evidenceRelevance!]}</span>
                    </li>
                  ))}
                </ul>
                <p className="fsa-phone-custody">
                  Items above are grouped under the source label {group.source}. Adding a label does not import a file or verify when it was created. A name-only item has no file or file history
                  to verify. Open "View details" to check each item's attachment status and recorded versions.
                </p>
              </div>
            ))}
          </div>
        )}

        {needsReviewDocs.length > 0 && (
          <div className="fsa-review-queue">
            <h3>Needs a relevance decision ({needsReviewDocs.length})</h3>
            <ul>
              {needsReviewDocs.map((doc) => (
                <li key={doc.id}>
                  <span>{doc.name}</span>
                  <div className="fsa-actions">
                    <button type="button" className="fsa-secondary" disabled={pendingFieldEditDocIds.has(doc.id)} onClick={() => markRelevance(activeMatter, doc.id, 'relevant')}>
                      Relevant
                    </button>
                    <button type="button" className="fsa-secondary" disabled={pendingFieldEditDocIds.has(doc.id)} onClick={() => markRelevance(activeMatter, doc.id, 'not_relevant')}>
                      Not relevant
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section className="fsa-panel" aria-labelledby="fsa-timeline-heading">
        <h2 id="fsa-timeline-heading">Timeline</h2>
        <ol className="fsa-timeline">
          {activeMatter.timeline
            .slice()
            .reverse()
            .map((entry) => (
              <li key={entry.id}>
                <span className="fsa-timeline-at">{friendlyTimestamp(entry.at)}</span>
                {entry.label}
                {entry.detail ? ` — ${entry.detail}` : ''}
              </li>
            ))}
        </ol>
      </section>

      <section className="fsa-panel" aria-labelledby="fsa-package-heading">
        <h2 id="fsa-package-heading">Attorney package</h2>

        {readinessFlags.length > 0 ? (
          <div className="fsa-readiness" role="status">
            <p className="fsa-readiness-title">Before you share, a few things to know:</p>
            <ul>
              {readinessFlags.map((flag) => (
                <li key={flag.id}>{flag.message}</li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="fsa-package-status">Everything here is organized — this package is ready to share.</p>
        )}

        <details className="fsa-packet-preview">
          <summary>Preview what the attorney will see</summary>
          {packetPreviewGroups.length === 0 ? (
            <p className="fsa-package-status">No documents to show yet.</p>
          ) : (
            <div className="fsa-packet-preview-body">
              <p className="fsa-packet-preview-name">{activeMatter.name}</p>
              <p className="fsa-doc-source">
                Reference {activeMatter.reference} · {activeMatter.documents.length} document{activeMatter.documents.length === 1 ? '' : 's'} · {packetPreviewGroups.length} categor
                {packetPreviewGroups.length === 1 ? 'y' : 'ies'}
              </p>
              {packetPreviewGroups.map((group) => (
                <div key={group.category} className="fsa-packet-preview-category">
                  <p className="fsa-packet-preview-category-name">
                    {group.category} ({group.documents.length})
                  </p>
                  <ul>
                    {group.documents.map((doc) => (
                      <li key={doc.id}>{doc.name}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </details>

        {activeMatter.documents.length > 0 && (
          <div className="fsa-actions">
            <button type="button" className="fsa-secondary" onClick={() => setShowPrintPreview(true)}>
              Print / save this preview
            </button>
          </div>
        )}

        {(activeMatter.attorneyPackage.status === 'not_created' || activeMatter.attorneyPackage.status === 'revoked') && (
          <>
            {activeMatter.attorneyPackage.status === 'revoked' && (
              <p className="fsa-package-status">Access was revoked {friendlyTimestamp(activeMatter.attorneyPackage.revokedAt ?? '')}. Create a new package to share again.</p>
            )}
            {capabilities.attorneySharesReady ? (
              <>
                <fieldset className="fsa-share-doc-picker">
                  <legend>Choose which documents to share with the attorney</legend>
                  {shareEligibleDocuments.length === 0 ? (
                    <p className="fsa-package-status">No documents are ready to share yet. Documents must finish upload and scanning first.</p>
                  ) : (
                    <ul className="fsa-share-doc-list">
                      {shareEligibleDocuments.map((doc) => (
                        <li key={doc.id}>
                          <label className="fsa-checkbox-row">
                            <input type="checkbox" checked={selectedShareDocIds.has(doc.id)} onChange={() => toggleShareDocSelected(doc.id)} />
                            {doc.name}
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </fieldset>
                <div className="fsa-actions">
                  <button type="button" className="fsa-primary" onClick={() => createPackage(activeMatter)} disabled={selectedShareDocIds.size === 0 || isBusy}>
                    Create attorney package
                  </button>
                </div>
              </>
            ) : (
              <p className="fsa-package-status" role="status">
                Sharing with your attorney isn't finished being set up for this environment yet — check back soon.
              </p>
            )}
          </>
        )}
        {(activeMatter.attorneyPackage.status === 'shared' || activeMatter.attorneyPackage.status === 'opened') && (
          <>
            <p className="fsa-package-status">{activeMatter.attorneyPackage.status === 'opened' ? `Opened by the attorney ${friendlyTimestamp(activeMatter.attorneyPackage.openedAt ?? '')}.` : 'Shared — not yet opened.'}</p>
            <p className="fsa-package-status">
              {(activeMatter.attorneyPackage.sharedDocumentIds ?? []).length} document{(activeMatter.attorneyPackage.sharedDocumentIds ?? []).length === 1 ? '' : 's'} in the shared package.
            </p>
            <a className="fsa-share-link" href={activeMatter.attorneyPackage.shareLink}>
              {activeMatter.attorneyPackage.shareLink}
            </a>
            {onPreviewAsAttorney && (
              <p className="fsa-crosslink">
                <button type="button" onClick={() => onPreviewAsAttorney(activeMatter.id)}>
                  See what the attorney sees (demo) →
                </button>
              </p>
            )}
            <div className="fsa-actions">
              {!showUpdateSharePanel && (
                <button type="button" className="fsa-secondary" onClick={() => startUpdatingShare(activeMatter)}>
                  Update shared package
                </button>
              )}
              <button type="button" className="fsa-secondary" onClick={() => revokePackage(activeMatter)} disabled={isBusy}>
                Revoke access
              </button>
            </div>
            {showUpdateSharePanel && (
              <fieldset className="fsa-share-doc-picker">
                <legend>Choose which documents the attorney can see</legend>
                {shareEligibleDocuments.length === 0 ? (
                  <p className="fsa-package-status">No documents are ready to share yet. Documents must finish upload and scanning first.</p>
                ) : (
                  <ul className="fsa-share-doc-list">
                    {shareEligibleDocuments.map((doc) => (
                      <li key={doc.id}>
                        <label className="fsa-checkbox-row">
                          <input type="checkbox" checked={selectedShareDocIds.has(doc.id)} onChange={() => toggleShareDocSelected(doc.id)} />
                          {doc.name}
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="fsa-actions">
                  <button type="button" className="fsa-secondary" onClick={() => setShowUpdateSharePanel(false)}>
                    Cancel
                  </button>
                  <button type="button" className="fsa-primary" onClick={() => saveShareUpdate(activeMatter)} disabled={selectedShareDocIds.size === 0 || isBusy}>
                    Save changes
                  </button>
                </div>
              </fieldset>
            )}
          </>
        )}
      </section>

      <section className="fsa-panel" aria-labelledby="fsa-plan-heading">
        <h2 id="fsa-plan-heading">Your plan</h2>
        <p className="fsa-package-status">Currently on the {TIER_LABEL[activeMatter.tier].toLowerCase()}.{capabilities.pricingReady ? ' Compare plans below, then switch anytime — nothing here charges a card.' : ''}</p>
        {!capabilities.pricingReady && (
          <p className="fsa-package-status" role="status">
            Paid plan upgrades aren't available yet for this environment — the free organizer above remains fully usable in the meantime.
          </p>
        )}
        <div className="fsa-plan-grid">
          {(Object.keys(TIER_LABEL) as MatterTier[]).map((tier) => (
            <div key={tier} className={`fsa-plan-card${activeMatter.tier === tier ? ' is-current' : ''}`}>
              <p className="fsa-plan-name">{TIER_LABEL[tier]}</p>
              <p className="fsa-plan-pitch">{TIER_PITCH[tier]}</p>
              {activeMatter.tier === tier ? (
                <span className="fsa-plan-current-badge">Current plan</span>
              ) : !capabilities.pricingReady ? null : tier === 'one_time' ? (
                <button type="button" className="fsa-secondary" onClick={() => upgrade(activeMatter, 'one_time')}>
                  Get a one-time prep pass
                </button>
              ) : tier === 'monthly' ? (
                <button type="button" className="fsa-secondary" onClick={() => upgrade(activeMatter, 'monthly')}>
                  Start monthly case workspace
                </button>
              ) : null}
            </div>
          ))}
        </div>
        {capabilities.pricingReady && (
          <p className="fsa-plan-note">Plan details may change. Available options reflect the current published offer, and switching here does not charge a card.</p>
        )}
      </section>

      <section className="fsa-panel" aria-labelledby="fsa-notifications-heading">
        <h2 id="fsa-notifications-heading">Notifications</h2>
        <label className="fsa-checkbox-row">
          <input type="checkbox" checked={activeMatter.notifyOnAttorneyOpen} disabled={pendingNotifyMatterIds.has(activeMatter.id)} onChange={(event) => toggleNotifyOnAttorneyOpen(activeMatter, event.target.checked)} />
          Email me when the attorney opens this package
        </label>
        <p className="fsa-package-status">No real email is sent in this preview — this only records your preference for when a real notifier is wired up.</p>
      </section>

      {capabilities.matterWatchReady && (
        <section className="fsa-panel" aria-labelledby="fsa-watch-heading">
          <h2 id="fsa-watch-heading">Matter Watch</h2>
          {activeMatter.matterWatch ? (
            <>
              <p className="fsa-package-status">
                Watching for {activeMatter.matterWatch.triggerTypes.map((triggerType) => MATTER_WATCH_TRIGGER_LABELS[triggerType]).join(', ')} matching {activeMatter.matterWatch.partyNames.join(', ')}{' '}
                in {activeMatter.matterWatch.court}, {activeMatter.matterWatch.county}.
              </p>
              <p className="fsa-package-status">No external court source is checked yet in this preview — this only saves your watch criteria for when a real docket-monitoring source is wired up.</p>
              <label className="fsa-checkbox-row">
                <input type="checkbox" checked={activeMatter.matterWatch.enabled} disabled={matterWatchBusy} onChange={(event) => toggleMatterWatchEnabled(activeMatter, event.target.checked)} />
                Actively watching
              </label>
              <div className="fsa-actions">
                <button type="button" className="fsa-secondary" disabled={matterWatchBusy} onClick={() => unsubscribeMatterWatch(activeMatter)}>
                  Stop watching this matter
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="fsa-package-status">
                Get notified if a new filing matches this matter — a complaint, summons, subpoena, or eviction notice tied to the same parties and court. No external court source is checked yet in
                this preview; subscribing only saves your watch criteria for when a real docket-monitoring source is wired up.
              </p>
              <div className="fsa-field">
                <label htmlFor="fsa-watch-party-names">Party names (comma-separated)</label>
                <input
                  id="fsa-watch-party-names"
                  value={matterWatchPartyNames}
                  onChange={(event) => setMatterWatchPartyNames(event.target.value)}
                  placeholder="Jane Doe, John Doe"
                  disabled={matterWatchBusy}
                />
              </div>
              <div className="fsa-field">
                <label htmlFor="fsa-watch-court">Court</label>
                <input id="fsa-watch-court" value={matterWatchCourt} onChange={(event) => setMatterWatchCourt(event.target.value)} disabled={matterWatchBusy} />
              </div>
              <div className="fsa-field">
                <label htmlFor="fsa-watch-county">County</label>
                <input id="fsa-watch-county" value={matterWatchCounty} onChange={(event) => setMatterWatchCounty(event.target.value)} disabled={matterWatchBusy} />
              </div>
              <fieldset>
                <legend>Notify me about</legend>
                {MATTER_WATCH_TRIGGER_TYPES.map((triggerType) => (
                  <label key={triggerType} className="fsa-checkbox-row">
                    <input type="checkbox" checked={matterWatchTriggerTypes.includes(triggerType)} disabled={matterWatchBusy} onChange={() => toggleMatterWatchTriggerType(triggerType)} />
                    {MATTER_WATCH_TRIGGER_LABELS[triggerType]}
                  </label>
                ))}
              </fieldset>
              <div className="fsa-actions">
                <button type="button" className="fsa-secondary" disabled={matterWatchBusy} onClick={() => subscribeMatterWatch(activeMatter)}>
                  {matterWatchBusy ? 'Saving…' : 'Notify me about this matter'}
                </button>
              </div>
            </>
          )}
        </section>
      )}

      <FenzSafeJurisdictionGuidancePanel onEscalation={setJurisdictionEscalation} />

      {archivedSection}
      {showPrintPreview && <FenzSafePacketPrintPreview matter={activeMatter} variant="draft" onClose={() => setShowPrintPreview(false)} />}
    </section>
  );
}
